import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Receipt, Plus, Trash2, Search, Eye, X, ShoppingCart, Printer, CheckCircle2, Edit, Minus, AlertTriangle, Download, FileText, Loader2 } from 'lucide-react';
import { formatCurrency, formatDateTimeDR, getLocalDateString } from '../utils/formatters';

const Invoices = ({ user }) => {
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [businessConfig, setBusinessConfig] = useState(null);
  const [ncfTypes, setNcfTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtros de fecha - por defecto fecha actual (zona horaria RD: UTC-4)
  const hoy = getLocalDateString();
  const [fechaDesde, setFechaDesde] = useState(hoy);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPriceList, setCustomerPriceList] = useState(null);
  const [consumidorFinalId, setConsumidorFinalId] = useState(null); // ID del Consumidor Final
  const [cart, setCart] = useState([]);
  const [formData, setFormData] = useState({
    tipo_ncf: 'B02',
    cliente_id: '',
    cliente_nombre: '',
    cliente_rnc: '',
    cliente_telefono: '',
    cliente_direccion: '',
    tipo_pago: 'efectivo',
    es_credito: false,
    dias_credito: 30,
    vendedor: user?.full_name || '',
    fecha: getLocalDateString(),
    observaciones: '',
  });
  
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [combinedPayments, setCombinedPayments] = useState({
    efectivo: 0,
    tarjeta: 0,
    transferencia: 0,
    cheque: 0,
  });
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productCodeInput, setProductCodeInput] = useState('');
  
  // Estado para búsqueda de cotizaciones
  const [quotationSearchOpen, setQuotationSearchOpen] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  // Estado para consulta RNC (DGII / Facturazo)
  const [rncLookupLoading, setRncLookupLoading] = useState(false);
  const [rncPreview, setRncPreview] = useState(null);

  // Consultar RNC en API externa (DGII / Facturazo)
  const handleRncLookup = async () => {
    const rnc = formData.cliente_rnc?.trim().replace(/-/g, '');
    if (!rnc || rnc.length < 9) {
      toast.error('Ingrese un RNC válido (mínimo 9 dígitos)');
      return;
    }
    setRncLookupLoading(true);
    setRncPreview(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/rnc/lookup`, {
        params: { rnc },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.found) {
        setRncPreview({ name: res.data.name, status: res.data.status });
      } else {
        toast.error('RNC no encontrado');
      }
    } catch {
      toast.error('Error al consultar RNC');
    } finally {
      setRncLookupLoading(false);
    }
  };

  const confirmRncName = () => {
    if (!rncPreview) return;
    const updates = { cliente_nombre: rncPreview.name };
    if (rncPreview.status === 'ACTIVO') {
      updates.tipo_ncf = 'B01';
    }
    setFormData(prev => ({ ...prev, ...updates }));
    setRncPreview(null);
    toast.success(rncPreview.status === 'ACTIVO' ? 'Cliente actualizado y NCF cambiado a B01' : 'Nombre del cliente actualizado');
  };

  const rejectRncName = () => {
    setRncPreview(null);
  };
  
  // Atajos de teclado
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!dialogOpen) return;
      
      // F2 - Abrir búsqueda de productos
      if (e.key === 'F2') {
        e.preventDefault();
        setProductSearchOpen(true);
      }
      
      // F3 - Focus en campo de código
      if (e.key === 'F3') {
        e.preventDefault();
        document.getElementById('product-code-input')?.focus();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [dialogOpen]);

  // Auto-focus en "Código Interno / Código de Barras" al abrir factura
  useEffect(() => {
    if (dialogOpen) {
      const t = setTimeout(() => {
        document.getElementById('product-code-input')?.focus();
      }, 150);
      return () => clearTimeout(t);
    }
  }, [dialogOpen]);

  const canCreate = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'cajero';
  const canCancel = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'gerente';

  useEffect(() => {
    fetchInvoices();
    fetchProducts();
    fetchCustomers();
    fetchBusinessConfig();
    fetchNCFTypes();
  }, []);

  // Recargar facturas cuando cambien los filtros de fecha
  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      fetchInvoices(fechaDesde, fechaHasta);
    }
  }, [fechaDesde, fechaHasta]);

  // Load CONSUMIDOR FINAL data when customers are loaded
  useEffect(() => {
    if (customers.length > 0 && !formData.cliente_nombre) {
      const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0" || c.nombre.toUpperCase().includes("CONSUMIDOR FINAL"));
      if (consumidorFinal) {
        // Guardar el ID del Consumidor Final para uso posterior
        setConsumidorFinalId(consumidorFinal.id);
        
        setFormData(prev => ({
          ...prev,
          cliente_id: '', // Vacío para permitir edición
          cliente_nombre: consumidorFinal.nombre, // PRELLENADO con "CONSUMIDOR FINAL" pero editable
          cliente_rnc: consumidorFinal.rnc || '', // Prellenado con RNC por defecto
          cliente_telefono: consumidorFinal.telefono || '',
          cliente_direccion: consumidorFinal.direccion || '',
          tipo_ncf: consumidorFinal.tipo_ncf_predeterminado || 'B02',
        }));
        setSelectedCustomer(null); // null indica que es Consumidor Final editable
        
        // Cargar lista de precios del cliente por defecto
        loadCustomerPriceList(consumidorFinal);
      }
    }
  }, [customers]);

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig(response.data);
    } catch (error) {
      console.error('Error loading business config');
    }
  };

  const fetchNCFTypes = async () => {
    try {
      const response = await axios.get(`${API}/ncf-types/sales`);
      setNcfTypes(response.data);
    } catch (error) {
      console.error('Error al cargar tipos NCF');
      // Fallback a tipo por defecto si falla
      setNcfTypes([{ code: 'B02', description: 'Consumidor Final' }]);
    }
  };

  const fetchInvoices = async (desde = fechaDesde, hasta = fechaHasta) => {
    try {
      // Construir URL con filtros de fecha
      const params = new URLSearchParams();
      
      // Convertir fechas de YYYY-MM-DD a DD/MM/AAAA
      if (desde) {
        const [year, month, day] = desde.split('-');
        const fechaFormateada = `${day}/${month}/${year}`;
        params.append('fecha_desde', fechaFormateada);
      }
      if (hasta) {
        const [year, month, day] = hasta.split('-');
        const fechaFormateada = `${day}/${month}/${year}`;
        params.append('fecha_hasta', fechaFormateada);
      }
      
      const response = await axios.get(`${API}/invoices?${params.toString()}`);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.filter(p => p.existencia > 0));
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setCustomers(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const searchCustomerByIdentifier = (searchValue) => {
    if (!searchValue) return null;
    
    const search = searchValue.trim().toLowerCase();
    return customers.find(c => 
      c.rnc?.toLowerCase().includes(search) ||
      c.cedula?.toLowerCase().includes(search) ||
      c.telefono?.includes(search) ||
      c.nombre?.toLowerCase().includes(search)
    );
  };

  const handleCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);
    
    // Búsqueda automática después de 3 caracteres
    if (value.length >= 3) {
      const found = searchCustomerByIdentifier(value);
      if (found) {
        selectCustomer(found);
      }
    }
  };

  const handleCustomerSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Abrir modal de búsqueda avanzada con el término prerellenado
      setCustomerSearchOpen(true);
      toast.info('Abriendo búsqueda avanzada de clientes...');
    }
  };

  // Función para cargar lista de precios de un cliente
  const loadCustomerPriceList = async (customer) => {
    if (customer.lista_precios_id) {
      try {
        const response = await axios.get(`${API}/price-lists/${customer.lista_precios_id}`);
        setCustomerPriceList(response.data);
        return response.data;
      } catch (error) {
        console.error('Error al cargar lista de precios:', error);
        setCustomerPriceList(null);
        return null;
      }
    } else {
      // Si no tiene lista asignada, buscar la lista General
      try {
        const response = await axios.get(`${API}/price-lists`);
        const generalList = response.data.find(list => list.es_general);
        if (generalList) {
          setCustomerPriceList(generalList);
          return generalList;
        }
      } catch (error) {
        console.error('Error al cargar lista General:', error);
      }
      setCustomerPriceList(null);
      return null;
    }
  };

  const selectCustomer = async (customer) => {
    setFormData(prev => ({
      ...prev,
      cliente_id: customer.id,
      cliente_nombre: customer.nombre,
      cliente_rnc: customer.rnc || '',
      cliente_telefono: customer.telefono || '',
      cliente_direccion: customer.direccion || '',
      tipo_ncf: customer.tipo_ncf_predeterminado || 'B02',
      es_credito: customer.tiene_credito || false,
      dias_credito: customer.dias_credito || 30,
    }));
    setSelectedCustomer(customer);
    setCustomerSearchOpen(false);
    setCustomerSearchTerm('');
    
    // Cargar lista de precios del cliente
    const priceList = await loadCustomerPriceList(customer);
    
    // Recalcular precios del carrito si hay productos
    if (cart.length > 0) {
      await recalculateCartPrices(customer.id);
    }
    
    // Mostrar mensaje apropiado
    if (priceList) {
      let message = `Cliente seleccionado: ${customer.nombre} (Lista: ${priceList.nombre})`;
      if (priceList.descuento_global > 0) {
        message += ` -${priceList.descuento_global}%`;
      }
      toast.success(message);
    } else {
      toast.success(`Cliente seleccionado: ${customer.nombre}`);
    }
  };

  const handleProductCodeSubmit = (e) => {
    e.preventDefault();
    if (!productCodeInput.trim()) return;
    
    // Buscar por código interno o código de barras
    const product = products.find(p => 
      p.codigo_interno === productCodeInput.trim() || 
      (Array.isArray(p.codigo_barras) ? p.codigo_barras.includes(productCodeInput.trim()) : p.codigo_barras === productCodeInput.trim())
    );
    
    if (product) {
      // Producto encontrado: flujo normal
      addToCart(product);
      setProductCodeInput('');
      toast.success(`${product.nombre} agregado`);
    } else {
      // Producto NO encontrado: abrir modal de búsqueda avanzada con el texto prerellenado
      setProductSearchTerm(productCodeInput.trim()); // Prerellenar búsqueda
      setProductSearchOpen(true); // Abrir modal
      setProductCodeInput(''); // Limpiar campo
      toast.info('Producto no encontrado. Abriendo búsqueda avanzada...');
    }
  };

  const handleCombinedPaymentSave = () => {
    const total = Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0);
    const invoiceTotal = calculateTotals().total;
    
    if (Math.abs(total - invoiceTotal) > 0.01) {
      toast.error(`El total del pago combinado (${formatCurrency(total)}) no coincide con el total de la factura (${formatCurrency(invoiceTotal)})`);
      return;
    }
    
    setFormData(prev => ({ ...prev, tipo_pago: 'combinado' }));
    setPaymentModalOpen(false);
    toast.success('Pago combinado configurado');
  };

  const filteredProductsForSearch = products.filter(p =>
    p.nombre.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.codigo_interno?.includes(productSearchTerm) ||
    (Array.isArray(p.codigo_barras) ? p.codigo_barras.some(b => b.includes(productSearchTerm)) : p.codigo_barras?.includes(productSearchTerm)) ||
    p.referencia?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.categoria?.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  const handlePrintInvoice = (invoice) => {
    // Función auxiliar para formatear fecha en impresión
    const formatPrintDate = (dateStr) => {
      if (!dateStr) {
        // Si no hay fecha, usar la fecha actual
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        return `${day}/${month}/${year}`;
      }
      
      // Si ya tiene el formato DD/MM/YYYY, extraer solo la fecha
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        return dateStr.split(' ')[0];
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        return `${day}/${month}/${year}`;
      }
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    
    const formatPrintTime = (dateStr) => {
      if (!dateStr) {
        // Si no hay fecha, usar la hora actual
        const now = new Date();
        let hours = now.getHours();
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
      }
      
      // Si ya tiene el formato con hora AM/PM (ej: "17/01/2026 07:44:00 AM")
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        if (parts.length >= 3) {
          const timePart = parts[1];
          const ampmPart = parts[2];
          const timeOnly = timePart.split(':').slice(0, 2).join(':');
          const ampmFormatted = ampmPart.toLowerCase() === 'pm' ? 'p. m.' : 'a. m.';
          return `${timeOnly} ${ampmFormatted}`;
        }
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        const now = new Date();
        let hours = now.getHours();
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
      }
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };
    
    // Usar valores de businessConfig o fallbacks
    const config = {
      logo: businessConfig?.logo || '',
      nombre_comercio: businessConfig?.nombre_comercio || businessConfig?.sistema_nombre || 'Nombre Comercial *',
      sistema_nombre: businessConfig?.sistema_nombre || 'Sistema de Gestión',
      rnc: businessConfig?.rnc || '',
      telefono: businessConfig?.telefono || '',
      direccion: businessConfig?.direccion || '',
      slogan: businessConfig?.slogan || '',
      mensaje_factura: businessConfig?.mensaje_factura || ''
    };

    // Mapa de descripción de NCF -> Título de la factura (estilo recibo POS)
    const ncfTitleMap = {
      'B01': 'FACTURA DE CREDITO FISCAL',
      'B02': 'FACTURA DE CONSUMO',
      'B03': 'NOTA DE DEBITO',
      'B04': 'NOTA DE CREDITO',
      'B14': 'FACTURA REGIMEN ESPECIAL',
      'B15': 'FACTURA GUBERNAMENTAL',
    };
    const tituloFactura = ncfTitleMap[invoice.tipo_ncf] || 'FACTURA';

    // Datos pago / cambio
    const formaPagoLabel = (tp) => {
      if (!tp) return 'Efectivo';
      const map = {
        efectivo: 'Efectivo',
        tarjeta: 'Tarjeta de Credito',
        transferencia: 'Transferencia',
        cheque: 'Cheque',
        credito: 'Credito',
        credito_favor: 'Nota de Credito'
      };
      return map[tp] || tp.toString().charAt(0).toUpperCase() + tp.toString().slice(1);
    };

    const numeroFactura = invoice.numero_factura || (invoice.id ? invoice.id.slice(0, 8) : '');
    const cajeroNombre = invoice.cajero_nombre || invoice.usuario_nombre || user?.full_name || '';
    // Barcode externo (tec-it) renderizando el número de factura
    const barcodeData = encodeURIComponent(numeroFactura);
    const barcodeUrl = `https://barcode.tec-it.com/barcode.ashx?data=${barcodeData}&code=Code128&dpi=96&translate-esc=true`;

    // Buscar fecha de vencimiento del NCF según tipo
    const ncfInfo = (ncfTypes || []).find(t => t.code === invoice.tipo_ncf);
    let validoHasta = '';
    if (ncfInfo?.fecha_vencimiento) {
      const fv = new Date(ncfInfo.fecha_vencimiento);
      if (!isNaN(fv.getTime())) {
        const d = String(fv.getDate()).padStart(2, '0');
        const m = String(fv.getMonth() + 1).padStart(2, '0');
        const y = fv.getFullYear();
        validoHasta = `${d}/${m}/${y}`;
      }
    }

    // Mapa de productos para determinar si aplica ITBIS por ítem
    const productMap = new Map((products || []).map(p => [p.id, p]));
    const tasaItbis = (businessConfig?.itbis_porcentaje ?? 18) / 100;
    const itbisHabilitado = businessConfig?.itbis_habilitado !== false;

    const printWindow = window.open('', '', 'width=320,height=600');

    // Diseño POS estilo recibo térmico 80mm (alineado con diseño solicitado)
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Factura ${invoice.numero_factura || invoice.id}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500;700&display=swap" rel="stylesheet">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }

            @page { size: 80mm auto; margin: 2mm; }

            @media print {
              body {
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }

            body {
              font-family: 'Roboto Mono', 'Consolas', 'Lucida Console', 'DejaVu Sans Mono', monospace;
              font-size: 13px;
              line-height: 1.45;
              width: 76mm;
              margin: 0 auto;
              padding: 3mm;
              color: #000;
              background: #fff;
              -webkit-font-smoothing: none;
              text-rendering: geometricPrecision;
            }

            .center { text-align: center; }
            .bold { font-weight: bold; }
            .right { text-align: right; }

            .store-name {
              font-size: 18px;
              font-weight: bold;
              letter-spacing: 1px;
              text-align: center;
              margin: 1mm 0 2mm 0;
            }

            .header-line {
              text-align: center;
              font-size: 12px;
              line-height: 1.3;
              margin: 0.3mm 0;
            }

            .logo {
              max-width: 45mm;
              max-height: 18mm;
              margin: 0 auto 2mm;
              display: block;
            }

            .sep {
              border-top: 1px dashed #000;
              margin: 2.5mm 0;
              height: 0;
            }

            .row {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 4mm;
              margin: 0.6mm 0;
              font-size: 12px;
              line-height: 1.35;
            }

            .row .label { white-space: nowrap; }
            .row .value { text-align: right; word-break: break-word; }

            .doc-title {
              text-align: center;
              font-size: 13px;
              font-weight: bold;
              margin: 2mm 0;
              letter-spacing: 0.4px;
            }

            .items-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              font-size: 12px;
              padding: 0.5mm 0;
            }
            .items-header .col-desc { flex: 2.6; text-align: left; }
            .items-header .col-itbis { flex: 1; text-align: right; padding-right: 1mm; }
            .items-header .col-neto { flex: 1.2; text-align: right; }
            .items-header .col-pct { flex: 0.4; text-align: right; }

            .item-block { margin: 1.5mm 0; }
            .item-line1 { font-size: 11px; line-height: 1.3; }
            .item-line2 { font-size: 12px; font-weight: bold; line-height: 1.3; }
            .item-detail {
              display: flex;
              justify-content: space-between;
              font-size: 11px;
              margin-top: 0.3mm;
              line-height: 1.3;
            }
            .item-detail .col-qtyprice { flex: 2.6; text-align: left; }
            .item-detail .col-itbis { flex: 1; text-align: right; padding-right: 1mm; }
            .item-detail .col-neto { flex: 1.2; text-align: right; }
            .item-detail .col-pct { flex: 0.4; text-align: right; }

            .barcode-wrap { text-align: center; margin: 3mm 0 1mm 0; }
            .barcode-wrap img { width: 55mm; height: 14mm; display: block; margin: 0 auto; }
            .barcode-num { font-size: 12px; font-weight: bold; margin-top: 0.5mm; }

            .footer-msg {
              text-align: center;
              font-size: 11px;
              line-height: 1.4;
              margin: 1mm 0;
            }

            .thanks {
              text-align: center;
              font-size: 13px;
              margin-top: 3mm;
              line-height: 1.4;
            }
            .thanks .store { font-weight: bold; font-size: 14px; margin-top: 1mm; }
          </style>
        </head>
        <body>
          <!-- Encabezado -->
          ${config.logo ? `<img src="${config.logo}" alt="Logo" class="logo" />` : ''}
          <div class="store-name">${(config.nombre_comercio || config.sistema_nombre).toUpperCase()}</div>
          ${config.direccion ? `<div class="header-line">${config.direccion}</div>` : ''}
          ${config.telefono ? `<div class="header-line">Teléfono: ${config.telefono}</div>` : ''}
          ${config.rnc ? `<div class="header-line">RNC: ${config.rnc}</div>` : ''}

          <div class="sep"></div>

          <!-- Fecha y NCF -->
          <div class="row"><span class="label">Fecha:</span><span class="value">${formatPrintDate(invoice.fecha)} ${formatPrintTime(invoice.fecha)}</span></div>
          <div class="row"><span class="label">NCF:</span><span class="value bold">${invoice.ncf || 'N/A'}</span></div>
          ${validoHasta ? `<div class="row"><span class="label">Valido Hasta:</span><span class="value">${validoHasta}</span></div>` : ''}

          <div class="sep"></div>

          <!-- Cliente -->
          <div class="row"><span class="label">Factura #:</span><span class="value bold">${numeroFactura}</span></div>
          <div class="row"><span class="label">Cliente:</span><span class="value">${(invoice.cliente_nombre || invoice.cliente?.nombre || 'CONSUMIDOR FINAL').toUpperCase()}</span></div>
          ${(invoice.cliente_rnc || invoice.cliente?.rnc) && (invoice.cliente_rnc || invoice.cliente?.rnc) !== '000000000' && (invoice.cliente_rnc || invoice.cliente?.rnc) !== '000-0000000-0' ? `
            <div class="row"><span class="label">RNC/CED:</span><span class="value">${invoice.cliente_rnc || invoice.cliente?.rnc}</span></div>
          ` : ''}

          <div class="doc-title">${tituloFactura}</div>

          <div class="sep"></div>

          <!-- Encabezado de items -->
          <div class="items-header">
            <span class="col-desc">DESCRIPCION</span>
            <span class="col-itbis">ITBIS</span>
            <span class="col-neto">NETO</span>
            <span class="col-pct">%</span>
          </div>

          <div class="sep"></div>

          <!-- Items -->
          ${(invoice.items || invoice.productos || []).map(item => {
            const precioOriginal = item.precio_original || (item.precio_unitario || item.precio);
            const precioFinal = item.precio_unitario || item.precio;
            const tieneDescuento = item.oferta_aplicada && precioOriginal > precioFinal;
            // Determinar si el producto aplica ITBIS
            const prod = productMap.get(item.producto_id);
            const aplicaItbis = itbisHabilitado && (prod ? prod.aplica_itbis !== false : true);
            // El subtotal del item incluye ITBIS si aplica
            const itbisItem = aplicaItbis ? Number((item.subtotal - (item.subtotal / (1 + tasaItbis))).toFixed(2)) : 0;
            const netoItem = aplicaItbis ? Number((item.subtotal / (1 + tasaItbis)).toFixed(2)) : item.subtotal;
            const pctTax = aplicaItbis ? `${Math.round(tasaItbis * 100)}` : 'E.';
            const codigoInterno = prod?.codigo_interno || item.codigo_interno || '';
            const nombreProducto = (item.producto_nombre || item.nombre || '').toUpperCase();
            const descripcion = codigoInterno ? `${codigoInterno} - ${nombreProducto}` : nombreProducto;

            return `
              <div class="item-block">
                <div class="item-line2">${descripcion}</div>
                <div class="item-detail">
                  <span class="col-qtyprice">${item.cantidad} x $${precioFinal.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  <span class="col-itbis">$${itbisItem.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  <span class="col-neto">$${netoItem.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  <span class="col-pct">${pctTax}</span>
                </div>
                ${tieneDescuento ? `<div class="item-line1" style="text-decoration: line-through;">Precio normal: $${precioOriginal.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>` : ''}
                ${item.oferta_aplicada ? `<div class="item-line2" style="border:1px solid #000;padding:1mm;margin-top:0.5mm;">** OFERTA: ${item.oferta_aplicada} **</div>` : ''}
              </div>
            `;
          }).join('')}

          <div class="sep"></div>

          <!-- Totales -->
          <div class="row"><span class="label">Sub Total</span><span class="value">$${invoice.subtotal.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
          ${invoice.itbis > 0 ? `<div class="row"><span class="label">ITBIS (18%)</span><span class="value">$${invoice.itbis.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>` : ''}
          <div class="row bold" style="font-size:13px;"><span class="label">Total</span><span class="value">$${invoice.total.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>

          <div class="sep"></div>

          <!-- Pagos -->
          ${invoice.es_credito ? `
            <div class="row"><span class="label">Crédito (${invoice.dias_credito || 30} días)</span><span class="value">$${invoice.total.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
          ` : (
            (invoice.formas_pago && invoice.formas_pago.length > 0) ?
              invoice.formas_pago.map(fp => `
                <div class="row"><span class="label">${formaPagoLabel(fp.tipo_pago)}</span><span class="value">$${(fp.monto || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
              `).join('') :
              `<div class="row"><span class="label">${formaPagoLabel(invoice.tipo_pago)}</span><span class="value">$${invoice.total.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>`
          )}
          ${invoice.tipo_pago === 'efectivo' && invoice.monto_recibido ? `
            <div class="row"><span class="label">Recibido</span><span class="value">$${(invoice.monto_recibido || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
            <div class="row"><span class="label">Cambio</span><span class="value">$${(invoice.cambio || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
          ` : `
            <div class="row"><span class="label">Cambio Pendiente</span><span class="value">$0.00</span></div>
          `}

          <!-- Barcode -->
          <div class="barcode-wrap">
            <img src="${barcodeUrl}" alt="Barcode" onerror="this.style.display='none'" />
          </div>

          <div class="sep"></div>

          <!-- Transacción -->
          <div class="row"><span class="label">Transaccion #:</span><span class="value">${numeroFactura}</span></div>
          <div class="row"><span class="label">Cajero:</span><span class="value">${cajeroNombre}</span></div>

          <div class="sep"></div>

          <!-- Mensaje al pie de página configurable desde Configuración del Comercio -->
          ${config.mensaje_factura ? `<div class="footer-msg">${config.mensaje_factura.replace(/\n/g, '<br/>')}</div>` : ''}

          <script>
            window.onload = function() {
              const doPrint = function() {
                window.print();
                setTimeout(function() { window.close(); }, 100);
              };
              // Esperar a que la fuente Roboto Mono cargue antes de imprimir
              if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(function() { setTimeout(doPrint, 200); });
              } else {
                setTimeout(doPrint, 800);
              }
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();

    toast.success('Imprimiendo factura...');
  };

  const handleCustomerSelect = (customerId) => {
    setRncPreview(null);
    if (customerId === "new") {
      // Load CONSUMIDOR FINAL data - SIEMPRE EDITABLE con campos prellenados
      const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0");
      setSelectedCustomer(null); // null = modo editable
      
      if (consumidorFinal) {
        setConsumidorFinalId(consumidorFinal.id); // Guardar ID para usar al guardar
        setFormData({
          ...formData,
          cliente_id: '', // Vacío para permitir edición de campos
          cliente_nombre: consumidorFinal.nombre, // PRELLENADO con "CONSUMIDOR FINAL" pero editable
          cliente_rnc: consumidorFinal.rnc || '', // Prellenado con RNC por defecto
          cliente_telefono: consumidorFinal.telefono || '',
          cliente_direccion: consumidorFinal.direccion || '',
          tipo_ncf: consumidorFinal.tipo_ncf_predeterminado || 'B02',
        });
        // Cargar lista de precios General
        loadCustomerPriceList(consumidorFinal);
      } else {
        // Fallback if CONSUMIDOR FINAL not found
        setFormData({
          ...formData,
          cliente_id: '',
          cliente_nombre: 'CONSUMIDOR FINAL', // Prellenado
          cliente_rnc: '000000000', // Prellenado
          cliente_telefono: '000-000-0000',
          cliente_direccion: 'SIN DIRECCION',
          tipo_ncf: 'B02',
        });
        setCustomerPriceList(null); // Lista General
      }
    } else {
      // Cliente registrado seleccionado
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setFormData({
          ...formData,
          cliente_id: customer.id, // ID real del cliente
          cliente_nombre: customer.nombre,
          cliente_rnc: customer.rnc || '',
          cliente_telefono: customer.telefono || '',
          cliente_direccion: customer.direccion || '',
          tipo_ncf: customer.tipo_ncf_predeterminado,
        });
        // Cargar lista de precios del cliente
        loadCustomerPriceList(customer);
      }
    }
  };


  const recalculateCartPrices = async (clienteId) => {
    if (cart.length === 0) return;
    
    try {
      const updatedCart = [];
      for (const item of cart) {
        // Obtener nuevo precio según cliente
        const response = await axios.get(
          `${API}/products/${item.producto_id}/price?cliente_id=${clienteId || ''}`
        );
        
        updatedCart.push({
          ...item,
          precio_unitario: response.data.precio_final,
          subtotal: item.cantidad * response.data.precio_final
        });
      }
      
      setCart(updatedCart);
      toast.success('Precios actualizados según lista del cliente');
    } catch (error) {
      console.error('Error al recalcular precios:', error);
      toast.error('Error al actualizar precios');
    }
  };

  const togglePriceType = (productId) => {
    // Solo permitir cambio de precio si estamos usando Lista General
    const isGeneralList = !customerPriceList || customerPriceList.es_general;
    
    if (!isGeneralList) {
      toast.error('Solo se puede cambiar el precio en la Lista General');
      return;
    }

    setCart(cart.map(item => {
      if (item.producto_id === productId) {
        const product = products.find(p => p.id === productId);
        
        if (!product || !product.precio_minimo) {
          toast.error('Este producto no tiene precio mínimo configurado');
          return item;
        }

        // Alternar entre precio normal y precio mínimo
        const isUsingMinPrice = item.precio_unitario === product.precio_minimo;
        const newPrice = isUsingMinPrice ? product.precio_venta : product.precio_minimo;
        
        return {
          ...item,
          precio_unitario: newPrice,
          subtotal: newPrice * item.cantidad,
          usando_precio_minimo: !isUsingMinPrice
        };
      }
      return item;
    }));
  };

  const addToCart = async (product) => {
    const existingItem = cart.find((item) => item.producto_id === product.id);
    
    // Obtener precio según lista del cliente
    let precio = product.precio_venta;
    try {
      const response = await axios.get(
        `${API}/products/${product.id}/price?cliente_id=${formData.cliente_id || ''}`
      );
      precio = response.data.precio_final;
    } catch (error) {
      console.error('Error al obtener precio:', error);
      // Si falla, usar precio base
      precio = product.precio_venta;
    }
    
    if (existingItem) {
      if (existingItem.cantidad >= product.existencia) {
        toast.error('No hay suficiente stock');
        return;
      }
      setCart(
        cart.map((item) =>
          item.producto_id === product.id
            ? { ...item, cantidad: item.cantidad + 1, subtotal: (item.cantidad + 1) * item.precio_unitario }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          producto_id: product.id,
          producto_nombre: product.nombre,
          cantidad: 1,
          precio_unitario: precio,
          precio_original: precio,
          subtotal: precio,
          usando_precio_minimo: false,
        },
      ]);
    }
  };

  const updateCartQuantity = (productId, newQuantity) => {
    const product = products.find(p => p.id === productId);
    if (newQuantity > product.existencia) {
      toast.error('No hay suficiente stock');
      return;
    }
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(
      cart.map((item) =>
        item.producto_id === productId
          ? { ...item, cantidad: newQuantity, subtotal: newQuantity * item.precio_unitario }
          : item
      )
    );
  };

  // Permite editar manualmente el precio unitario, validando contra el precio mínimo
  // del producto (o el precio_venta registrado si no hay mínimo configurado).
  const updateCartPrice = (productId, newPrice) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const precioRegistrado = product.precio_venta || 0;
    const precioMinimo = product.precio_minimo && product.precio_minimo > 0
      ? product.precio_minimo
      : precioRegistrado;

    let precioFinal = parseFloat(newPrice);
    if (isNaN(precioFinal) || precioFinal <= 0) {
      precioFinal = precioRegistrado;
      toast.error('Precio inválido. Se restablece el precio del producto.');
    } else if (precioFinal < precioMinimo) {
      toast.error(
        `El precio no puede ser menor al ${product.precio_minimo ? 'precio mínimo' : 'precio del producto'} (${formatCurrency(precioMinimo)})`
      );
      precioFinal = precioMinimo;
    }

    setCart(
      cart.map((item) =>
        item.producto_id === productId
          ? {
              ...item,
              precio_unitario: precioFinal,
              subtotal: precioFinal * item.cantidad,
              usando_precio_minimo: !!product.precio_minimo && precioFinal === product.precio_minimo,
            }
          : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.producto_id !== productId));
  };

  const calculateTotals = () => {
    // El precio de venta YA INCLUYE el ITBIS
    const totalConItbis = cart.reduce((sum, item) => sum + item.subtotal, 0);
    
    if (businessConfig?.itbis_habilitado) {
      // Calcular el subtotal SIN ITBIS (precio base)
      // Formula: subtotal = total / (1 + tasa_itbis)
      const tasaItbis = businessConfig.itbis_porcentaje / 100;
      const subtotal = totalConItbis / (1 + tasaItbis);
      const itbis = totalConItbis - subtotal;
      
      return { 
        subtotal: Math.round(subtotal * 100) / 100, 
        itbis: Math.round(itbis * 100) / 100, 
        total: totalConItbis 
      };
    } else {
      // Si ITBIS no está habilitado, el precio es el subtotal
      return { 
        subtotal: totalConItbis, 
        itbis: 0, 
        total: totalConItbis 
      };
    }
  };

  // Descuento total (informativo): diferencia entre el precio normal/lista de cada
  // producto (precio_original) y el precio efectivo cobrado, cuando se vendió por debajo.
  const calculateDescuento = () => {
    const desc = cart.reduce((sum, item) => {
      const original = (item.precio_original != null ? item.precio_original : item.precio_unitario);
      const diff = (original - item.precio_unitario) * item.cantidad;
      return sum + (diff > 0 ? diff : 0);
    }, 0);
    return Math.round(desc * 100) / 100;
  };

  const getErrorMessage = (error) => {
    // Handle Pydantic validation errors (array of objects)
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      return error.response.data.detail.map(err => err.msg).join(', ');
    }
    
    // Handle simple string errors
    if (typeof error.response?.data?.detail === 'string') {
      return error.response.data.detail;
    }
    
    // Handle object errors
    if (typeof error.response?.data?.detail === 'object') {
      return JSON.stringify(error.response.data.detail);
    }
    
    // Default error message
    return 'Error al procesar la solicitud';
  };

  const handleCreateInvoice = async () => {
    if (cart.length === 0) {
      toast.error('Agregue productos a la factura');
      return;
    }

    // Fallback: si el nombre del cliente quedó vacío (p. ej. no cargó CONSUMIDOR FINAL),
    // usar "CONSUMIDOR FINAL" en lugar de bloquear silenciosamente el botón
    if (!formData.cliente_nombre.trim()) {
      setFormData(prev => ({ ...prev, cliente_nombre: 'CONSUMIDOR FINAL' }));
    }

    if (formData.es_credito && !formData.cliente_id) {
      toast.error('Para ventas a crédito debe seleccionar un cliente registrado');
      return;
    }

    // Validar límite de crédito disponible
    if (formData.es_credito && selectedCustomer && (selectedCustomer.credito_disponible || 0) < calculateTotals().total) {
      toast.error('El total de la factura supera el crédito disponible del cliente');
      return;
    }

    // Abrir diálogo de confirmación antes de crear la factura
    setConfirmCreateOpen(true);
  };

  const confirmAndCreateInvoice = async () => {
    try {
      setCreatingInvoice(true);
      const invoiceData = {
        tipo_ncf: formData.tipo_ncf,
        cliente: {
          nombre: formData.cliente_nombre,
          rnc: formData.cliente_rnc,
          telefono: formData.cliente_telefono,
          direccion: formData.cliente_direccion,
        },
        items: cart.map((item) => {
          const original = (item.precio_original != null ? item.precio_original : item.precio_unitario);
          const lineDesc = Math.max(0, (original - item.precio_unitario) * item.cantidad);
          return {
            ...item,
            precio_original: original,
            descuento: Math.round(lineDesc * 100) / 100,
          };
        }),
        // Si es crédito, tipo_pago debe ser "credito"
        tipo_pago: formData.es_credito ? "credito" : formData.tipo_pago,
        es_credito: formData.es_credito,
        dias_credito: formData.dias_credito,
        observaciones: formData.observaciones,
        origen: "invoicing",
      };

      // Determinar el cliente_id a usar:
      // - Si hay cliente_id en formData, usar ese (cliente registrado seleccionado)
      // - Si no hay cliente_id pero sí consumidorFinalId, usar el del Consumidor Final (cliente casual con datos editados)
      if (formData.cliente_id) {
        invoiceData.cliente_id = formData.cliente_id;
      } else if (consumidorFinalId) {
        // Cliente casual: usar ID del Consumidor Final pero con nombre/RNC personalizados
        invoiceData.cliente_id = consumidorFinalId;
      }

      // Include combined payments if applicable
      if (formData.tipo_pago === 'combinado') {
        invoiceData.pagos_combinados = combinedPayments;
      }

      const response = await axios.post(`${API}/invoices`, invoiceData);
      const createdInvoice = response.data;
      
      // Si la factura fue creada desde una cotización, actualizar su estado
      if (selectedQuotation) {
        try {
          await axios.put(
            `${API}/quotations/${selectedQuotation.id}/mark-converted?factura_numero=${createdInvoice.numero_factura}`
          );
          toast.success(`✅ Cotización ${selectedQuotation.numero_cotizacion} convertida a factura ${createdInvoice.numero_factura}`);
        } catch (error) {
          console.error('Error al actualizar cotización:', error);
          // No bloquear el flujo si falla la actualización de la cotización
        }
      }
      
      toast.success('Factura creada exitosamente');
      setConfirmCreateOpen(false);
      setDialogOpen(false);
      resetForm();
      fetchInvoices();
      fetchProducts();
      fetchCustomers(); // Refresh to update credit status
      
      // Si la factura quedó pendiente de cobro, NO imprimir - avisar y salir
      if (createdInvoice.status === 'pendiente') {
        toast.info('Factura enviada a Caja para cobro. Se imprimirá desde el módulo de Caja.', { duration: 5000 });
        return;
      }

      // Automatically print invoice
      setTimeout(() => {
        printInvoice(createdInvoice);
      }, 500);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      console.error('Error completo:', error.response?.data);
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (window.confirm('¿Está seguro de anular esta factura?')) {
      try {
        await axios.patch(`${API}/invoices/${invoiceId}/cancel`);
        toast.success('Factura anulada');
        fetchInvoices();
        fetchProducts();
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        toast.error(errorMessage);
      }
    }
  };

  const resetForm = () => {
    // Buscar Consumidor Final para cargar sus datos por defecto
    const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0");
    setRncPreview(null);
    
    if (consumidorFinal) {
      setConsumidorFinalId(consumidorFinal.id);
      setFormData({
        tipo_ncf: consumidorFinal.tipo_ncf_predeterminado || 'B02',
        cliente_id: '', // Vacío para permitir edición
        cliente_nombre: consumidorFinal.nombre, // PRELLENADO con "CONSUMIDOR FINAL" pero editable
        cliente_rnc: consumidorFinal.rnc || '', // Prellenado con RNC por defecto
        cliente_telefono: consumidorFinal.telefono || '',
        cliente_direccion: consumidorFinal.direccion || '',
        tipo_pago: 'efectivo',
        es_credito: false,
        dias_credito: 30,
        vendedor: user?.full_name || '',
        fecha: getLocalDateString(),
        observaciones: '',
      });
      // Cargar lista de precios General
      loadCustomerPriceList(consumidorFinal);
    } else {
      // Fallback si no se encuentra Consumidor Final
      setFormData({
        tipo_ncf: 'B02',
        cliente_id: '',
        cliente_nombre: 'CONSUMIDOR FINAL', // Prellenado
        cliente_rnc: '000000000', // Prellenado
        cliente_telefono: '',
        cliente_direccion: '',
        tipo_pago: 'efectivo',
        es_credito: false,
        dias_credito: 30,
        vendedor: user?.full_name || '',
        fecha: getLocalDateString(),
        observaciones: '',
      });
      setCustomerPriceList(null);
    }
    
    setSelectedCustomer(null);
    setSelectedQuotation(null);
    setCart([]);
    setCombinedPayments({ efectivo: 0, tarjeta: 0, transferencia: 0, cheque: 0 });
    setProductCodeInput('');
    setProductSearchTerm('');
  };

  const viewInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailDialogOpen(true);
  };

  // ============ FUNCIONES DE COTIZACIONES ============
  
  const fetchQuotations = async () => {
    setLoadingQuotations(true);
    try {
      const response = await axios.get(`${API}/quotations?status=pendiente`);
      setQuotations(response.data);
    } catch (error) {
      toast.error('Error al cargar cotizaciones');
      console.error(error);
    } finally {
      setLoadingQuotations(false);
    }
  };

  const loadQuotationToForm = async (quotation) => {
    try {
      // Cargar cliente
      const customer = customers.find(c => c.id === quotation.cliente_id);
      if (customer) {
        await selectCustomer(customer);
      } else {
        // Si no se encuentra el cliente registrado, usar la info de la cotización
        setFormData(prev => ({
          ...prev,
          cliente_id: quotation.cliente_id || '',
          cliente_nombre: quotation.cliente.nombre,
          cliente_rnc: quotation.cliente.rnc || '',
          cliente_telefono: quotation.cliente.telefono || '',
          cliente_direccion: quotation.cliente.direccion || '',
        }));
      }

      // Cargar productos al carrito
      // Obtener precios actuales de los productos
      const updatedCart = [];
      let hasPriceChanges = false;

      for (const item of quotation.items) {
        const currentProduct = products.find(p => p.id === item.producto_id);
        if (currentProduct) {
          const currentPrice = currentProduct.precio_venta;
          const quotedPrice = item.precio_unitario;
          
          if (currentPrice !== quotedPrice) {
            hasPriceChanges = true;
            toast.warning(
              `⚠️ Precio actualizado: ${item.producto_nombre}\n` +
              `Cotizado: ${formatCurrency(quotedPrice)} → Actual: ${formatCurrency(currentPrice)}`,
              { duration: 5000 }
            );
          }

          updatedCart.push({
            producto_id: item.producto_id,
            producto_nombre: item.producto_nombre,
            cantidad: item.cantidad,
            precio_unitario: currentPrice, // Usar precio ACTUAL
            subtotal: currentPrice * item.cantidad,
          });
        }
      }

      setCart(updatedCart);
      setSelectedQuotation(quotation);
      setQuotationSearchOpen(false);

      if (hasPriceChanges) {
        toast.info('💡 Los precios han sido actualizados a los valores actuales');
      } else {
        toast.success(`Cotización ${quotation.numero_cotizacion} cargada`);
      }

      // Actualizar observaciones para indicar que viene de una cotización
      setFormData(prev => ({
        ...prev,
        observaciones: `Convertida de cotización ${quotation.numero_cotizacion}${prev.observaciones ? '\n' + prev.observaciones : ''}`
      }));

    } catch (error) {
      toast.error('Error al cargar cotización');
      console.error(error);
    }
  };

  const printInvoice = (invoice) => {
    // Reutilizar el mismo formato POS 80mm
    handlePrintInvoice(invoice);
  };

  // Función para descargar factura en PDF formato A4 - MISMO DISEÑO que impresión
  const downloadInvoicePDF = async (invoice) => {
    const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
    const clientCode = getInvoiceClientCode(invoice);
    const subtotalSinItbis = invoice.subtotal || (invoice.total - (invoice.itbis || 0));
    const esCredito = invoice.cliente.rnc && invoice.cliente.rnc !== '000000000';
    const tipoComprobante = esCredito ? 'CRÉDITO FISCAL' : 'CONSUMIDOR FINAL';
    
    // Función para formatear fecha/hora
    const formatPrintDateTime = (dateStr) => {
      if (!dateStr) return '-';
      
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        if (parts.length >= 3) {
          const datePart = parts[0];
          const timePart = parts[1];
          const ampmPart = parts[2];
          const timeOnly = timePart.split(':').slice(0, 2).join(':');
          const ampmFormatted = ampmPart.toLowerCase() === 'pm' ? 'p.m.' : 'a.m.';
          return `${datePart} ${timeOnly} ${ampmFormatted}`;
        }
        return dateStr;
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr || '-';
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };
    
    const fechaFormateada = formatPrintDateTime(invoice.fecha);
    
    // Función para formatear moneda con separador de miles
    const formatMoney = (value) => {
      return (value || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    };
    
    toast.info('Generando PDF...');
    
    // Crear iframe oculto para renderizar el contenido
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position: fixed; top: 0; left: 0; width: 210mm; height: 297mm; border: none; visibility: hidden;';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    
    // Usar EXACTAMENTE el mismo HTML/CSS que printInvoiceA4
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Helvetica Neue', Arial, sans-serif; 
              font-size: 10px;
              line-height: 1.4;
              color: #333;
              background: #fff;
              padding: 15mm;
              width: 210mm;
              min-height: 297mm;
            }
            
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding-bottom: 20px;
              border-bottom: 2px solid #000;
              margin-bottom: 20px;
            }
            .company { max-width: 60%; }
            .company-name {
              font-size: 22px;
              font-weight: 700;
              letter-spacing: -0.5px;
              margin-bottom: 8px;
            }
            .company-info {
              font-size: 9px;
              color: #666;
              line-height: 1.6;
            }
            .invoice-header { text-align: right; }
            .invoice-title {
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 2px;
              color: #666;
              margin-bottom: 5px;
            }
            .invoice-number {
              font-size: 20px;
              font-weight: 700;
              margin-bottom: 10px;
            }
            .invoice-type {
              font-size: 9px;
              text-transform: uppercase;
              letter-spacing: 1px;
              padding: 8px 15px;
              border: 1px solid #333;
              display: inline-block;
              text-align: center;
              line-height: 1.8;
            }
            .invoice-type span {
              display: block;
              margin-top: 4px;
            }
            
            .details-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              margin-bottom: 25px;
            }
            .detail-section h3 {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #eee;
            }
            .detail-row {
              display: flex;
              margin-bottom: 6px;
              font-size: 10px;
            }
            .detail-label {
              width: 80px;
              color: #666;
            }
            .detail-value {
              flex: 1;
              font-weight: 500;
            }
            
            .table-section { margin-bottom: 25px; }
            .table-title {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            thead th {
              text-align: left;
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              padding: 10px 8px;
              border-bottom: 2px solid #333;
              font-weight: 600;
            }
            thead th.right { text-align: right; }
            thead th.center { text-align: center; }
            tbody td {
              padding: 10px 8px;
              border-bottom: 1px solid #eee;
              font-size: 10px;
            }
            tbody td.right { 
              text-align: right; 
              font-family: 'Courier New', monospace;
            }
            tbody td.center { text-align: center; }
            tbody tr:last-child td {
              border-bottom: 2px solid #333;
            }
            .item-count {
              font-size: 9px;
              color: #666;
              margin-top: 10px;
              text-align: right;
            }
            
            .totals-section {
              display: flex;
              justify-content: flex-end;
              margin-bottom: 30px;
            }
            .totals-box { width: 250px; }
            .totals-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              font-size: 10px;
            }
            .totals-row.subtotal { border-bottom: 1px solid #eee; }
            .totals-row.total {
              border-top: 2px solid #333;
              margin-top: 5px;
              padding-top: 12px;
            }
            .totals-row.total .label,
            .totals-row.total .value {
              font-size: 14px;
              font-weight: 700;
            }
            .totals-row .label { color: #666; }
            .totals-row .value {
              font-family: 'Courier New', monospace;
              font-weight: 500;
            }
            
            .footer {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              padding-top: 20px;
              border-top: 1px solid #eee;
            }
            .footer-section h4 {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 15px;
            }
            .signature-area {
              height: 60px;
              border-bottom: 1px solid #333;
            }
            .signature-label {
              font-size: 8px;
              color: #999;
              text-align: center;
              margin-top: 5px;
            }
            .footer-info {
              font-size: 9px;
              color: #666;
              line-height: 1.6;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company">
              <div class="company-name">${config.nombre_comercio || 'LICORSTORE RD'}</div>
              <div class="company-info">
                ${config.rnc ? `RNC: ${config.rnc}<br>` : ''}
                ${config.direccion ? `${config.direccion}<br>` : ''}
                ${config.telefono ? `Tel: ${config.telefono}` : ''}
              </div>
            </div>
            <div class="invoice-header">
              <div class="invoice-title">Factura</div>
              <div class="invoice-number">${invoice.numero_factura}</div>
              <div class="invoice-type">${tipoComprobante}${invoice.ncf && invoice.ncf !== 'N/A' ? `<span style="font-family: 'Courier New', monospace; font-size: 10px; font-weight: 600;">NCF: ${invoice.ncf}</span>` : ''}</div>
            </div>
          </div>
          
          <div class="details-grid">
            <div class="detail-section">
              <h3>Cliente</h3>
              <div class="detail-row">
                <span class="detail-label">Código</span>
                <span class="detail-value">${clientCode}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Nombre</span>
                <span class="detail-value">${invoice.cliente.nombre}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">RNC/Cédula</span>
                <span class="detail-value">${invoice.cliente.rnc || invoice.cliente.cedula || '—'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Teléfono</span>
                <span class="detail-value">${invoice.cliente.telefono || '—'}</span>
              </div>
            </div>
            <div class="detail-section">
              <h3>Factura</h3>
              <div class="detail-row">
                <span class="detail-label">Fecha</span>
                <span class="detail-value">${fechaFormateada}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Condición</span>
                <span class="detail-value">${invoice.es_credito ? 'Crédito' : 'Contado'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Pago</span>
                <span class="detail-value">${invoice.es_credito ? 'Crédito' : (invoice.tipo_pago || 'Efectivo')}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Vendedor</span>
                <span class="detail-value">${invoice.cajero_nombre || 'Admin'}</span>
              </div>
            </div>
          </div>
          
          <div class="table-section">
            <div class="table-title">Detalle</div>
            <table>
              <thead>
                <tr>
                  <th style="width:30px" class="center">#</th>
                  <th>Descripción</th>
                  <th class="right" style="width:80px">Precio</th>
                  <th class="center" style="width:50px">Cant.</th>
                  <th class="right" style="width:80px">Subtotal</th>
                  <th class="right" style="width:70px">ITBIS</th>
                  <th class="right" style="width:90px">Total</th>
                </tr>
              </thead>
              <tbody>
                ${invoice.items.map((item, index) => {
                  const itemSubtotal = item.precio_unitario * item.cantidad;
                  const itemItbis = item.itbis || (itemSubtotal * 0.18);
                  const itemTotal = itemSubtotal + itemItbis;
                  return `
                    <tr>
                      <td class="center">${index + 1}</td>
                      <td>${item.producto_nombre}</td>
                      <td class="right">${formatMoney(item.precio_unitario)}</td>
                      <td class="center">${item.cantidad}</td>
                      <td class="right">${formatMoney(itemSubtotal)}</td>
                      <td class="right">${formatMoney(itemItbis || 0)}</td>
                      <td class="right">${formatMoney(itemTotal)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            <div class="item-count">${invoice.items.length} artículo(s)</div>
          </div>
          
          <div class="totals-section">
            <div class="totals-box">
              <div class="totals-row subtotal">
                <span class="label">Subtotal</span>
                <span class="value">RD$ ${formatMoney(subtotalSinItbis)}</span>
              </div>
              ${(() => {
                const d = (invoice.items || []).reduce((s, it) => s + (it.descuento != null ? it.descuento : Math.max(0, ((it.precio_original != null ? it.precio_original : it.precio_unitario) - it.precio_unitario) * it.cantidad)), 0);
                return d > 0 ? `<div class="totals-row"><span class="label">Descuento</span><span class="value">RD$ ${formatMoney(d)}</span></div>` : '';
              })()}
              <div class="totals-row">
                <span class="label">ITBIS (18%)</span>
                <span class="value">RD$ ${formatMoney(invoice.itbis || 0)}</span>
              </div>
              <div class="totals-row total">
                <span class="label">Total</span>
                <span class="value">RD$ ${formatMoney(invoice.total)}</span>
              </div>
            </div>
          </div>
          
          <div class="footer">
            <div class="footer-section">
              <h4>Elaborado por</h4>
              <div class="footer-info">
                ${invoice.cajero_nombre || 'Administrador'}<br>
                ${config.nombre_comercio || ''}
              </div>
            </div>
            <div class="footer-section">
              <h4>Recibido por</h4>
              <div class="signature-area"></div>
              <div class="signature-label">Firma</div>
            </div>
          </div>
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Esperar a que el iframe cargue
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;
      
      // Capturar el contenido del iframe
      const canvas = await html2canvas(iframeDoc.body, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794,
        height: 1123
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      
      // Nombre del archivo: NOMBRE_CLIENTE_FAC_00000029.pdf
      const clienteName = invoice.cliente.nombre
        .toUpperCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remover acentos
        .replace(/[^A-Z0-9\s]/g, '') // Solo letras, números y espacios
        .replace(/\s+/g, '_'); // Espacios por guiones bajos
      const numeroFactura = invoice.numero_factura.replace('FAC-', '');
      const fileName = `${clienteName}_FAC_${numeroFactura}.pdf`;
      
      pdf.save(fileName);
      
      toast.success(`PDF descargado: ${fileName}`);
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar el PDF');
    } finally {
      document.body.removeChild(iframe);
    }
  };

  // Función de impresión formato A4 (para imprimir directamente)
  const printInvoiceA4 = (invoice) => {
    const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
    const printWindow = window.open('', '', 'width=900,height=700');
    
    const clientCode = getInvoiceClientCode(invoice);
    const subtotalSinItbis = invoice.subtotal || (invoice.total - (invoice.itbis || 0));
    const esCredito = invoice.cliente.rnc && invoice.cliente.rnc !== '000000000';
    const tipoComprobante = esCredito ? 'CRÉDITO FISCAL' : 'CONSUMIDOR FINAL';
    
    // Función para formatear fecha/hora
    const formatPrintDateTime = (dateStr) => {
      if (!dateStr) return '-';
      
      // Si ya tiene el formato DD/MM/YYYY HH:MM:SS AM/PM, usarlo directamente
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        if (parts.length >= 3) {
          const datePart = parts[0];
          const timePart = parts[1];
          const ampmPart = parts[2];
          const timeOnly = timePart.split(':').slice(0, 2).join(':');
          const ampmFormatted = ampmPart.toLowerCase() === 'pm' ? 'p.m.' : 'a.m.';
          return `${datePart} ${timeOnly} ${ampmFormatted}`;
        }
        return dateStr;
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr || '-';
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };
    
    // Formatear la fecha de la factura
    const fechaFormateada = formatPrintDateTime(invoice.fecha);
    
    // Función para formatear moneda con separador de miles
    const formatMoney = (value) => {
      return (value || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    };
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Factura ${invoice.numero_factura}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: A4; margin: 15mm; }
            @media print { 
              body { margin: 0 !important; } 
            }
            body { 
              font-family: 'Helvetica Neue', Arial, sans-serif; 
              font-size: 10px;
              line-height: 1.4;
              color: #333;
              background: #fff;
              padding: 20mm;
            }
            
            /* Header */
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding-bottom: 20px;
              border-bottom: 2px solid #000;
              margin-bottom: 20px;
            }
            .company {
              max-width: 60%;
            }
            .company-name {
              font-size: 22px;
              font-weight: 700;
              letter-spacing: -0.5px;
              margin-bottom: 8px;
            }
            .company-info {
              font-size: 9px;
              color: #666;
              line-height: 1.6;
            }
            .invoice-header {
              text-align: right;
            }
            .invoice-title {
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 2px;
              color: #666;
              margin-bottom: 5px;
            }
            .invoice-number {
              font-size: 20px;
              font-weight: 700;
              margin-bottom: 10px;
            }
            .invoice-type {
              font-size: 9px;
              text-transform: uppercase;
              letter-spacing: 1px;
              padding: 8px 15px;
              border: 1px solid #333;
              display: inline-block;
              text-align: center;
              line-height: 1.8;
            }
            .invoice-type span {
              display: block;
              margin-top: 4px;
            }
            
            /* Details Grid */
            .details-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              margin-bottom: 25px;
            }
            .detail-section h3 {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #eee;
            }
            .detail-row {
              display: flex;
              margin-bottom: 6px;
              font-size: 10px;
            }
            .detail-label {
              width: 80px;
              color: #666;
            }
            .detail-value {
              flex: 1;
              font-weight: 500;
            }
            
            /* NCF */
            .ncf-box {
              background: #f9f9f9;
              padding: 10px 15px;
              margin-top: 10px;
              border-left: 3px solid #333;
            }
            .ncf-label {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
            }
            .ncf-value {
              font-size: 12px;
              font-weight: 600;
              font-family: 'Courier New', monospace;
            }
            
            /* Table */
            .table-section {
              margin-bottom: 25px;
            }
            .table-title {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            thead th {
              text-align: left;
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              padding: 10px 8px;
              border-bottom: 2px solid #333;
              font-weight: 600;
            }
            thead th.right { text-align: right; }
            thead th.center { text-align: center; }
            tbody td {
              padding: 10px 8px;
              border-bottom: 1px solid #eee;
              font-size: 10px;
            }
            tbody td.right { 
              text-align: right; 
              font-family: 'Courier New', monospace;
            }
            tbody td.center { text-align: center; }
            tbody tr:last-child td {
              border-bottom: 2px solid #333;
            }
            .item-count {
              font-size: 9px;
              color: #666;
              margin-top: 10px;
              text-align: right;
            }
            
            /* Totals */
            .totals-section {
              display: flex;
              justify-content: flex-end;
              margin-bottom: 30px;
            }
            .totals-box {
              width: 250px;
            }
            .totals-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              font-size: 10px;
            }
            .totals-row.subtotal {
              border-bottom: 1px solid #eee;
            }
            .totals-row.total {
              border-top: 2px solid #333;
              margin-top: 5px;
              padding-top: 12px;
            }
            .totals-row.total .label,
            .totals-row.total .value {
              font-size: 14px;
              font-weight: 700;
            }
            .totals-row .label {
              color: #666;
            }
            .totals-row .value {
              font-family: 'Courier New', monospace;
              font-weight: 500;
            }
            
            /* Footer */
            .footer {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              padding-top: 20px;
              border-top: 1px solid #eee;
            }
            .footer-section h4 {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 15px;
            }
            .signature-area {
              height: 60px;
              border-bottom: 1px solid #333;
            }
            .signature-label {
              font-size: 8px;
              color: #999;
              text-align: center;
              margin-top: 5px;
            }
            .footer-info {
              font-size: 9px;
              color: #666;
              line-height: 1.6;
            }
            
            /* Observations */
            .observations {
              margin-top: 15px;
              padding: 10px;
              background: #f9f9f9;
              font-size: 9px;
            }
            .obs-label {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              margin-bottom: 5px;
            }
          </style>
        </head>
        <body>
          <!-- Header -->
          <div class="header">
            <div class="company">
              <div class="company-name">${config.nombre_comercio || 'LICORSTORE RD'}</div>
              <div class="company-info">
                ${config.rnc ? `RNC: ${config.rnc}<br>` : ''}
                ${config.direccion ? `${config.direccion}<br>` : ''}
                ${config.telefono ? `Tel: ${config.telefono}` : ''}
              </div>
            </div>
            <div class="invoice-header">
              <div class="invoice-title">Factura</div>
              <div class="invoice-number">${invoice.numero_factura}</div>
              <div class="invoice-type">${tipoComprobante}${invoice.ncf && invoice.ncf !== 'N/A' ? `<span style="font-family: 'Courier New', monospace; font-size: 10px; font-weight: 600;">NCF: ${invoice.ncf}</span>` : ''}</div>
            </div>
          </div>
          
          <!-- Details Grid -->
          <div class="details-grid">
            <div class="detail-section">
              <h3>Cliente</h3>
              <div class="detail-row">
                <span class="detail-label">Código</span>
                <span class="detail-value">${clientCode}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Nombre</span>
                <span class="detail-value">${invoice.cliente.nombre}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">RNC/Cédula</span>
                <span class="detail-value">${invoice.cliente.rnc || invoice.cliente.cedula || '—'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Teléfono</span>
                <span class="detail-value">${invoice.cliente.telefono || '—'}</span>
              </div>
            </div>
            <div class="detail-section">
              <h3>Factura</h3>
              <div class="detail-row">
                <span class="detail-label">Fecha</span>
                <span class="detail-value">${fechaFormateada}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Condición</span>
                <span class="detail-value">${invoice.es_credito ? 'Crédito' : 'Contado'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Pago</span>
                <span class="detail-value">${invoice.es_credito ? 'Crédito' : (invoice.tipo_pago || 'Efectivo')}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Vendedor</span>
                <span class="detail-value">${invoice.cajero_nombre || 'Admin'}</span>
              </div>
            </div>
          </div>
          
          <!-- Products Table -->
          <div class="table-section">
            <div class="table-title">Detalle</div>
            <table>
              <thead>
                <tr>
                  <th style="width:30px">#</th>
                  <th>Descripción</th>
                  <th class="right" style="width:80px">Precio</th>
                  <th class="center" style="width:50px">Cant.</th>
                  <th class="right" style="width:80px">Subtotal</th>
                  <th class="right" style="width:70px">ITBIS</th>
                  <th class="right" style="width:90px">Total</th>
                </tr>
              </thead>
              <tbody>
                ${invoice.items.map((item, index) => {
                  const itemSubtotal = item.precio_unitario * item.cantidad;
                  const itemItbis = item.itbis || (itemSubtotal * 0.18);
                  const itemTotal = itemSubtotal + itemItbis;
                  return `
                    <tr>
                      <td class="center">${index + 1}</td>
                      <td>${item.producto_nombre}</td>
                      <td class="right">${formatMoney(item.precio_unitario)}</td>
                      <td class="center">${item.cantidad}</td>
                      <td class="right">${formatMoney(itemSubtotal)}</td>
                      <td class="right">${formatMoney(itemItbis || 0)}</td>
                      <td class="right">${formatMoney(itemTotal)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            <div class="item-count">${invoice.items.length} artículo(s)</div>
          </div>
          
          <!-- Totals -->
          <div class="totals-section">
            <div class="totals-box">
              <div class="totals-row subtotal">
                <span class="label">Subtotal</span>
                <span class="value">RD$ ${formatMoney(subtotalSinItbis)}</span>
              </div>
              ${(() => {
                const d = (invoice.items || []).reduce((s, it) => s + (it.descuento != null ? it.descuento : Math.max(0, ((it.precio_original != null ? it.precio_original : it.precio_unitario) - it.precio_unitario) * it.cantidad)), 0);
                return d > 0 ? `<div class="totals-row"><span class="label">Descuento</span><span class="value">RD$ ${formatMoney(d)}</span></div>` : '';
              })()}
              <div class="totals-row">
                <span class="label">ITBIS (18%)</span>
                <span class="value">RD$ ${formatMoney(invoice.itbis || 0)}</span>
              </div>
              <div class="totals-row total">
                <span class="label">Total</span>
                <span class="value">RD$ ${formatMoney(invoice.total)}</span>
              </div>
            </div>
          </div>
          
          ${invoice.observaciones ? `
          <div class="observations">
            <div class="obs-label">Observaciones</div>
            ${invoice.observaciones}
          </div>
          ` : ''}
          
          <!-- Footer -->
          <div class="footer">
            <div class="footer-section">
              <h4>Elaborado por</h4>
              <div class="footer-info">
                ${invoice.cajero_nombre || 'Administrador'}<br>
                ${config.nombre_comercio || ''}
              </div>
            </div>
            <div class="footer-section">
              <h4>Recibido por</h4>
              <div class="signature-area"></div>
              <div class="signature-label">Firma</div>
            </div>
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 100);
              }, 300);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Helper para obtener código de cliente - primero de la factura, luego por nombre o RNC
  const getClientCode = (clientNameOrInvoice) => {
    // Si es un objeto de factura con cliente que tiene codigo_cliente, usarlo directamente
    if (typeof clientNameOrInvoice === 'object' && clientNameOrInvoice?.cliente?.codigo_cliente) {
      return clientNameOrInvoice.cliente.codigo_cliente;
    }
    
    // Si es un objeto cliente con codigo_cliente
    if (typeof clientNameOrInvoice === 'object' && clientNameOrInvoice?.codigo_cliente) {
      return clientNameOrInvoice.codigo_cliente;
    }
    
    // Si es string (nombre del cliente), buscar en la lista de clientes
    const clientName = typeof clientNameOrInvoice === 'string' ? clientNameOrInvoice : clientNameOrInvoice?.nombre;
    const client = customers.find(c => 
      c.nombre?.toLowerCase() === clientName?.toLowerCase()
    );
    return client?.codigo_cliente || '-';
  };

  // Helper para obtener código de cliente de una factura específica
  const getInvoiceClientCode = (invoice) => {
    // Primero intentar obtener el código directamente de la factura
    if (invoice?.cliente?.codigo_cliente) {
      return invoice.cliente.codigo_cliente;
    }
    
    // Si tiene RNC, buscar por RNC (incluyendo 000000000 para CONSUMIDOR FINAL)
    if (invoice?.cliente?.rnc) {
      const clientByRnc = customers.find(c => c.rnc === invoice.cliente.rnc);
      if (clientByRnc?.codigo_cliente) {
        return clientByRnc.codigo_cliente;
      }
    }
    
    // Buscar por cédula
    if (invoice?.cliente?.cedula) {
      const clientByCedula = customers.find(c => c.cedula === invoice.cliente.cedula);
      if (clientByCedula?.codigo_cliente) {
        return clientByCedula.codigo_cliente;
      }
    }
    
    // Finalmente buscar por nombre
    return getClientCode(invoice?.cliente?.nombre);
  };

  const filteredInvoices = invoices.filter(
    (inv) =>
      inv.numero_factura.includes(searchTerm) ||
      inv.ncf.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getInvoiceClientCode(inv).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Facturación</h1>
          <p className="text-slate-600 mt-1">Gestiona las facturas y ventas</p>
        </div>

        {canCreate && (
          <div className="flex gap-3">
            <Button 
              className="bg-blue-500 hover:bg-blue-600" 
              onClick={() => {
                fetchQuotations();
                setQuotationSearchOpen(true);
              }}
            >
              <Search className="mr-2" size={18} />
              Buscar Cotización
            </Button>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-invoice-btn">
                  <Receipt className="mr-2" size={18} />
                  Nueva Factura
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto" data-testid="create-invoice-dialog">
              <DialogHeader>
                <DialogTitle className="text-2xl">Crear Nueva Factura</DialogTitle>
                <DialogDescription>Módulo de Facturación Tradicional</DialogDescription>
              </DialogHeader>
              
              {/* Indicador de cotización cargada */}
              {selectedQuotation && (
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <CheckCircle2 size={24} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-1">
                      📋 Factura desde Cotización {selectedQuotation.numero_cotizacion}
                    </h4>
                    <p className="text-sm text-blue-700">
                      Los datos han sido cargados desde la cotización. Los precios se han actualizado a los valores actuales.
                      Puede modificar los datos antes de crear la factura.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedQuotation(null);
                      resetForm();
                    }}
                    className="flex-shrink-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                  >
                    <X size={18} />
                  </Button>
                </div>
              )}
              
              {/* ENCABEZADO DE FACTURA */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-4 border border-slate-200">
                <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">Encabezado de Factura</h3>
                
                <div className="grid grid-cols-4 gap-4">
                  {/* Búsqueda rápida de cliente */}
                  <div className="col-span-2">
                    <Label htmlFor="customer-quick-search">Buscar Cliente (Código/RNC/Cédula/Teléfono/Nombre)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="customer-quick-search"
                        value={customerSearchTerm}
                        onChange={handleCustomerSearch}
                        onKeyDown={handleCustomerSearchKeyDown}
                        placeholder="Escriba código, nombre, RNC, cédula o teléfono..."
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        onClick={() => setCustomerSearchOpen(true)}
                        variant="outline"
                        className="whitespace-nowrap"
                      >
                        <Search size={16} className="mr-1" />
                        Buscar
                      </Button>
                    </div>
                  </div>

                  {/* Fecha */}
                  <div>
                    <Label htmlFor="fecha">Fecha *</Label>
                    <Input
                      id="fecha"
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                      required
                    />
                  </div>

                  {/* Vendedor */}
                  <div>
                    <Label htmlFor="vendedor">Vendedor</Label>
                    <Input
                      id="vendedor"
                      value={formData.vendedor}
                      readOnly
                      className="bg-slate-100"
                    />
                  </div>
                </div>

                {/* Cliente seleccionado */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cliente_nombre">Cliente Seleccionado *</Label>
                    <Input
                      id="cliente_nombre"
                      value={formData.cliente_nombre}
                      onChange={(e) => setFormData({ ...formData, cliente_nombre: e.target.value })}
                      readOnly={formData.cliente_id !== ''}
                      disabled={formData.cliente_id !== ''}
                      className={formData.cliente_id === '' ? "bg-white font-semibold" : "bg-slate-50 font-semibold"}
                      placeholder={formData.cliente_id === '' ? "Digite el nombre del cliente..." : ""}
                    />
                    {customerPriceList && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                          💰 Lista: {customerPriceList.nombre}
                        </span>
                        {customerPriceList.descuento_global > 0 && (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">
                            -{customerPriceList.descuento_global}% Global
                          </span>
                        )}
                      </div>
                    )}
                    {!customerPriceList && formData.cliente_nombre && (
                      <div className="mt-1">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded">
                          💰 Lista: General (Por defecto)
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cliente_rnc">RNC/Cédula</Label>
                    <div className="flex gap-2">
                      <Input
                        id="cliente_rnc"
                        value={formData.cliente_rnc}
                        onChange={(e) => { setFormData({ ...formData, cliente_rnc: e.target.value }); setRncPreview(null); }}
                        readOnly={formData.cliente_id !== ''}
                        disabled={formData.cliente_id !== ''}
                        className={`flex-1 ${formData.cliente_id === '' ? "bg-white" : "bg-slate-50"}`}
                        placeholder={formData.cliente_id === '' ? "Digite el RNC/Cédula..." : ""}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !formData.cliente_id) { e.preventDefault(); handleRncLookup(); } }}
                        data-testid="invoice-cliente-rnc-input"
                      />
                      <button
                        type="button"
                        data-testid="invoice-rnc-lookup-btn"
                        onClick={handleRncLookup}
                        disabled={!!formData.cliente_id || rncLookupLoading || !formData.cliente_rnc?.trim()}
                        className="h-10 w-10 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white flex items-center justify-center transition-colors shrink-0"
                        title="Consultar RNC en DGII"
                      >
                        {rncLookupLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                      </button>
                    </div>
                    {rncPreview && (
                      <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="invoice-rnc-preview">
                        <div className="min-w-0">
                          <p className="text-[10px] tracking-[0.1em] uppercase font-bold text-emerald-600 mb-0.5">RNC Encontrado en DGII</p>
                          <p className="text-sm font-bold text-slate-900 leading-tight">{rncPreview.name}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">Estado: {rncPreview.status}</p>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            data-testid="invoice-rnc-confirm-btn"
                            onClick={confirmRncName}
                            className="flex-1 h-8 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors"
                          >
                            Confirmar
                          </button>
                          <button
                            type="button"
                            data-testid="invoice-rnc-reject-btn"
                            onClick={rejectRncName}
                            className="flex-1 h-8 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-bold transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tipo de Comprobante y Forma de Pago */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="tipo_ncf">Tipo de Comprobante *</Label>
                    <Select 
                      value={formData.tipo_ncf} 
                      onValueChange={(value) => setFormData({ ...formData, tipo_ncf: value })}
                    >
                      <SelectTrigger id="tipo_ncf">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ncfTypes.length > 0 ? (
                          ncfTypes.map((ncfType) => (
                            <SelectItem key={ncfType.code} value={ncfType.code}>
                              {ncfType.code} - {ncfType.description}
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="B01">B01 - Crédito Fiscal</SelectItem>
                            <SelectItem value="B02">B02 - Consumidor Final</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="tipo_pago">Forma de Pago *</Label>
                    <Select 
                      value={formData.es_credito ? 'credito' : formData.tipo_pago} 
                      onValueChange={(value) => {
                        if (value === 'combinado') {
                          setPaymentModalOpen(true);
                        } else if (value === 'credito') {
                          // Activar venta a crédito
                          setFormData({ ...formData, tipo_pago: 'credito', es_credito: true });
                        } else {
                          // Desactivar venta a crédito si selecciona otra opción
                          setFormData({ ...formData, tipo_pago: value, es_credito: false });
                        }
                      }}
                    >
                      <SelectTrigger id="tipo_pago">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">💵 Efectivo</SelectItem>
                        <SelectItem value="tarjeta">💳 Tarjeta</SelectItem>
                        <SelectItem value="transferencia">🏦 Transferencia</SelectItem>
                        <SelectItem value="cheque">📝 Cheque</SelectItem>
                        <SelectItem value="credito">📋 Crédito</SelectItem>
                        <SelectItem value="combinado">🔀 Pago Combinado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.es_credito && selectedCustomer && (
                    <div>
                      <Label>Crédito Disponible</Label>
                      <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <span className="text-sm font-semibold text-blue-800">
                          {formatCurrency(selectedCustomer.credito_disponible || 0)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Validación de crédito */}
                {formData.es_credito && selectedCustomer && (
                  <div className={`p-3 rounded-lg border ${
                    (selectedCustomer.credito_disponible || 0) < calculateTotals().total 
                      ? 'bg-red-50 border-red-300' 
                      : 'bg-green-50 border-green-300'
                  }`}>
                    <div className="flex items-center gap-2">
                      {(selectedCustomer.credito_disponible || 0) < calculateTotals().total ? (
                        <>
                          <AlertTriangle size={20} className="text-red-600" />
                          <div className="text-sm text-red-800">
                            <strong>Crédito Insuficiente:</strong> El cliente no tiene crédito disponible suficiente.
                            <div className="mt-1">
                              Límite: {formatCurrency(selectedCustomer.limite_credito || 0)} | 
                              Disponible: {formatCurrency(selectedCustomer.credito_disponible || 0)} | 
                              Total factura: {formatCurrency(calculateTotals().total)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 size={20} className="text-green-600" />
                          <div className="text-sm text-green-800">
                            <strong>Crédito Disponible</strong>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* SELECCIÓN DE PRODUCTOS */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">Selección de Productos</h3>
                
                {/* Campo de código y atajos */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label htmlFor="product-code-input">Código Interno / Código de Barras</Label>
                    <form onSubmit={handleProductCodeSubmit} className="flex gap-2">
                      <Input
                        id="product-code-input"
                        value={productCodeInput}
                        onChange={(e) => setProductCodeInput(e.target.value)}
                        placeholder="Escriba o escanee el código..."
                        className="flex-1"
                      />
                      <Button type="submit" variant="outline">
                        Agregar
                      </Button>
                    </form>
                  </div>
                  <Button 
                    type="button" 
                    onClick={() => setProductSearchOpen(true)}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    <Search size={16} className="mr-2" />
                    Búsqueda Avanzada (F2)
                  </Button>
                </div>

                <div className="text-xs text-slate-500 flex gap-4">
                  <span>💡 <kbd className="px-2 py-1 bg-slate-100 rounded">F2</kbd> Búsqueda avanzada</span>
                  <span>💡 <kbd className="px-2 py-1 bg-slate-100 rounded">F3</kbd> Focus en código</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {/* TABLA DE PRODUCTOS */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg text-slate-800 flex items-center gap-2">
                    <ShoppingCart size={20} />
                    Productos en Factura ({cart.length})
                  </h3>
                  
                  {cart.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
                      <ShoppingCart size={48} className="mx-auto text-slate-400 mb-3" />
                      <p className="text-slate-600">No hay productos agregados</p>
                      <p className="text-sm text-slate-500 mt-1">Use el campo de código o búsqueda avanzada (F2)</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 border-b">
                          <tr>
                            <th className="text-left p-2 font-semibold">Cód. Interno</th>
                            <th className="text-left p-2 font-semibold">Producto</th>
                            <th className="text-center p-2 font-semibold w-24">Cantidad</th>
                            <th className="text-right p-2 font-semibold">Precio</th>
                            <th className="text-center p-2 font-semibold w-28">Tipo Precio</th>
                            <th className="text-right p-2 font-semibold">Importe</th>
                            <th className="text-center p-2 font-semibold">Stock</th>
                            <th className="text-center p-2 font-semibold w-20">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cart.map((item, index) => {
                            const subtotalLinea = item.precio_unitario * item.cantidad;
                            const producto = products.find(p => p.id === item.producto_id);
                            const stockBajo = producto && producto.existencia < 10;
                            
                            return (
                              <tr key={item.producto_id} className="border-b hover:bg-slate-50">
                                <td className="p-2 text-slate-700">{producto?.codigo_interno || '-'}</td>
                                <td className="p-2">
                                  <div className="font-medium text-slate-800">{item.producto_nombre}</div>
                                  <div className="text-xs text-slate-500">{producto?.unidad_medida}</div>
                                </td>
                                <td className="p-2">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => updateCartQuantity(item.producto_id, item.cantidad - 1)}
                                      className="h-7 w-7 p-0"
                                    >
                                      <Minus size={14} />
                                    </Button>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={item.cantidad}
                                      onChange={(e) => updateCartQuantity(item.producto_id, parseInt(e.target.value) || 1)}
                                      className="w-16 h-7 text-center"
                                    />
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => updateCartQuantity(item.producto_id, item.cantidad + 1)}
                                      className="h-7 w-7 p-0"
                                    >
                                      <Plus size={14} />
                                    </Button>
                                  </div>
                                </td>
                                <td className="p-2 text-right">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    defaultValue={item.precio_unitario}
                                    key={`price-${item.producto_id}-${item.precio_unitario}`}
                                    onBlur={(e) => {
                                      const v = parseFloat(e.target.value);
                                      if (!isNaN(v) && v !== item.precio_unitario) {
                                        updateCartPrice(item.producto_id, v);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                                    }}
                                    className="h-8 w-28 text-right font-medium ml-auto"
                                    data-testid={`cart-price-input-${item.producto_id}`}
                                  />
                                  <div className="text-[10px] text-slate-500 mt-1 text-right">
                                    {producto?.precio_minimo > 0 && (
                                      <>Mín: {formatCurrency(producto.precio_minimo)}</>
                                    )}
                                  </div>
                                </td>
                                <td className="p-2 text-center">
                                  {(!customerPriceList || customerPriceList.es_general) && producto?.precio_minimo ? (
                                    <div className="flex flex-col gap-1">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant={item.usando_precio_minimo ? "outline" : "default"}
                                        onClick={() => togglePriceType(item.producto_id)}
                                        className={`h-6 text-xs px-2 ${
                                          item.usando_precio_minimo ? 'bg-white' : 'bg-emerald-500 hover:bg-emerald-600'
                                        }`}
                                        title="Usar precio normal"
                                      >
                                        Normal
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant={item.usando_precio_minimo ? "default" : "outline"}
                                        onClick={() => togglePriceType(item.producto_id)}
                                        className={`h-6 text-xs px-2 ${
                                          item.usando_precio_minimo ? 'bg-blue-500 hover:bg-blue-600' : 'bg-white'
                                        }`}
                                        title={`Usar precio mínimo (${formatCurrency(producto.precio_minimo)})`}
                                      >
                                        Mínimo
                                      </Button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="p-2 text-right font-bold text-green-600">{formatCurrency(subtotalLinea)}</td>
                                <td className="p-2 text-center">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    stockBajo ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {producto?.existencia || 0}
                                  </span>
                                </td>
                                <td className="p-2 text-center">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeFromCart(item.producto_id)}
                                    className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* OBSERVACIONES */}
                <div className="space-y-2">
                  <Label htmlFor="observaciones">Observaciones / Notas</Label>
                  <Textarea
                    id="observaciones"
                    value={formData.observaciones || ''}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, observaciones: e.target.value });
                      }
                    }}
                    rows={3}
                    placeholder="Notas internas, comentarios del cliente, instrucciones especiales..."
                    className="resize-none"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Máximo 500 caracteres</span>
                    <span className={(formData.observaciones || '').length > 450 ? 'text-red-600 font-semibold' : ''}>
                      {(formData.observaciones || '').length} / 500
                    </span>
                  </div>
                </div>

                {/* TOTALES */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 rounded-lg border-2 border-slate-300">
                  <h3 className="font-semibold text-lg text-slate-800 mb-4 border-b pb-2">Resumen de Factura</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-base">
                      <span className="text-slate-700">Subtotal:</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(totals.subtotal)}</span>
                    </div>
                    
                    {calculateDescuento() > 0 && (
                      <div className="flex justify-between items-center text-base" data-testid="invoice-descuento-row">
                        <span className="text-amber-700">Descuento total:</span>
                        <span className="font-semibold text-amber-700">- {formatCurrency(calculateDescuento())}</span>
                      </div>
                    )}
                    
                    {businessConfig?.itbis_habilitado && (
                      <div className="flex justify-between items-center text-base">
                        <span className="text-slate-700">ITBIS ({businessConfig.itbis_porcentaje}%):</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(totals.itbis)}</span>
                      </div>
                    )}
                    
                    <div className="border-t-2 border-slate-400 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-slate-900">TOTAL:</span>
                        <span className="text-3xl font-bold text-green-600">{formatCurrency(totals.total)}</span>
                      </div>
                    </div>
                    
                    {formData.tipo_pago === 'combinado' && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                        <div className="text-sm font-semibold text-blue-800 mb-2">Desglose Pago Combinado:</div>
                        <div className="space-y-1 text-sm">
                          {combinedPayments.efectivo > 0 && (
                            <div className="flex justify-between">
                              <span>Efectivo:</span>
                              <span className="font-semibold">{formatCurrency(combinedPayments.efectivo)}</span>
                            </div>
                          )}
                          {combinedPayments.tarjeta > 0 && (
                            <div className="flex justify-between">
                              <span>Tarjeta:</span>
                              <span className="font-semibold">{formatCurrency(combinedPayments.tarjeta)}</span>
                            </div>
                          )}
                          {combinedPayments.transferencia > 0 && (
                            <div className="flex justify-between">
                              <span>Transferencia:</span>
                              <span className="font-semibold">{formatCurrency(combinedPayments.transferencia)}</span>
                            </div>
                          )}
                          {combinedPayments.cheque > 0 && (
                            <div className="flex justify-between">
                              <span>Cheque:</span>
                              <span className="font-semibold">{formatCurrency(combinedPayments.cheque)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Botón de Crear Factura */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleCreateInvoice}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-lg py-6"
                    disabled={creatingInvoice}
                    data-testid="submit-invoice-btn-main"
                  >
                    <Receipt className="mr-2" size={20} />
                    Crear Factura {cart.length > 0 && `(${formatCurrency(totals.total)})`}
                  </Button>
                </div>

                {/* Cart and Form (Legacy - keeping for compatibility) */}
                <div className="space-y-4 hidden">
                  <div className="space-y-3">
                    <Label htmlFor="tipo_ncf">Tipo NCF</Label>
                    <Select value={formData.tipo_ncf} onValueChange={(value) => setFormData({ ...formData, tipo_ncf: value })}>
                      <SelectTrigger data-testid="form-tipo-ncf-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="B01">B01 - Crédito Fiscal</SelectItem>
                        <SelectItem value="B02">B02 - Consumidor Final</SelectItem>
                      </SelectContent>
                    </Select>

                    <Label htmlFor="customer_select">Seleccionar Cliente Registrado (Opcional)</Label>
                    <Select value={formData.cliente_id || "new"} onValueChange={handleCustomerSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar cliente..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">Cliente nuevo / Manual</SelectItem>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.nombre} {customer.tiene_credito && `(Crédito: ${formatCurrency(customer.credito_disponible)})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {selectedCustomer && selectedCustomer.tiene_credito && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                        <div className="font-semibold text-emerald-800">Cliente con Crédito</div>
                        <div className="text-emerald-700 mt-1">
                          Límite: {formatCurrency(selectedCustomer.limite_credito)}
                        </div>
                        <div className="text-emerald-700">
                          Disponible: {formatCurrency(selectedCustomer.credito_disponible)}
                        </div>
                      </div>
                    )}

                    <Label htmlFor="cliente_nombre">Cliente *</Label>
                    <Input
                      id="cliente_nombre"
                      value={formData.cliente_nombre}
                      onChange={(e) => setFormData({ ...formData, cliente_nombre: e.target.value })}
                      placeholder="Nombre del cliente"
                      data-testid="form-cliente-nombre-input"
                      disabled={!!formData.cliente_id}
                    />

                    <Label htmlFor="cliente_rnc">RNC</Label>
                    <Input
                      id="cliente_rnc"
                      value={formData.cliente_rnc}
                      onChange={(e) => setFormData({ ...formData, cliente_rnc: e.target.value })}
                      placeholder="RNC (opcional)"
                      data-testid="form-cliente-rnc-input"
                      disabled={!!formData.cliente_id}
                    />

                    <Label htmlFor="tipo_ncf">Tipo NCF</Label>
                    <Select 
                      value={formData.tipo_ncf} 
                      onValueChange={(value) => setFormData({ ...formData, tipo_ncf: value })}
                      disabled={!businessConfig?.ncf_habilitado || !!formData.cliente_id}
                    >
                      <SelectTrigger id="tipo_ncf" data-testid="form-tipo-ncf-select">
                        <SelectValue placeholder="Seleccionar tipo NCF" />
                      </SelectTrigger>
                      <SelectContent>
                        {ncfTypes.length > 0 ? (
                          ncfTypes.map((ncfType) => (
                            <SelectItem key={ncfType.code} value={ncfType.code}>
                              {ncfType.code} - {ncfType.description}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="B02">B02 - Consumidor Final</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {!businessConfig?.ncf_habilitado && (
                      <p className="text-xs text-slate-500 mt-1">NCF deshabilitado en configuración</p>
                    )}
                    {ncfTypes.length === 0 && businessConfig?.ncf_habilitado && (
                      <p className="text-xs text-amber-600 mt-1">⚠️ No hay tipos NCF registrados en Gestión de Comprobantes</p>
                    )}

                    <Label htmlFor="tipo_pago">Forma de Pago</Label>
                    <Select value={formData.tipo_pago} onValueChange={(value) => setFormData({ ...formData, tipo_pago: value })}>
                      <SelectTrigger data-testid="form-tipo-pago-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex items-center space-x-2 pt-2">
                      <input
                        type="checkbox"
                        id="es_credito"
                        checked={formData.es_credito}
                        onChange={(e) => {
                          const isCredito = e.target.checked;
                          setFormData({ 
                            ...formData, 
                            es_credito: isCredito,
                            tipo_pago: isCredito ? 'credito' : 'efectivo'
                          });
                        }}
                        className="w-4 h-4"
                        data-testid="form-es-credito-checkbox"
                      />
                      <Label htmlFor="es_credito" className="cursor-pointer">Venta a Crédito</Label>
                    </div>

                    {formData.es_credito && (
                      <>
                        <Label htmlFor="dias_credito">Días de Crédito</Label>
                        <Input
                          id="dias_credito"
                          type="number"
                          value={formData.dias_credito}
                          onChange={(e) => setFormData({ ...formData, dias_credito: parseInt(e.target.value) })}
                          min="1"
                          data-testid="form-dias-credito-input"
                        />
                      </>
                    )}
                  </div>

                  {/* Cart Items */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Carrito ({cart.length})</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {cart.map((item) => (
                        <div key={item.producto_id} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded" data-testid={`cart-item-${item.producto_id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate text-xs">{item.producto_nombre}</div>
                            <div className="text-xs text-slate-600">{formatCurrency(item.precio_unitario)}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="1"
                              value={item.cantidad}
                              onChange={(e) => updateCartQuantity(item.producto_id, parseInt(e.target.value))}
                              className="w-14 h-7 text-xs"
                              data-testid={`cart-quantity-${item.producto_id}`}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeFromCart(item.producto_id)}
                              className="h-7 w-7 p-0"
                              data-testid={`cart-remove-${item.producto_id}`}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(totals.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>ITBIS (18%):</span>
                      <span>{formatCurrency(totals.itbis)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span className="text-green-600">{formatCurrency(totals.total)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCreateInvoice}
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    disabled={cart.length === 0}
                    data-testid="submit-invoice-btn"
                  >
                    Crear Factura
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Filtros de Rango de Fecha */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fecha Desde */}
          <div>
            <Label htmlFor="fecha-desde" className="text-sm font-medium text-slate-700 mb-1 block">
              Fecha Desde
            </Label>
            <Input
              id="fecha-desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Fecha Hasta */}
          <div>
            <Label htmlFor="fecha-hasta" className="text-sm font-medium text-slate-700 mb-1 block">
              Fecha Hasta
            </Label>
            <Input
              id="fecha-hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Búsqueda en Tiempo Real */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          placeholder="Buscar facturas por número, cliente, NCF..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-invoices-input"
        />
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="custom-table">
            <thead>
              <tr>
                <th>No. Factura</th>
                <th>NCF</th>
                <th>Código</th>
                <th>Cliente</th>
                <th>Fecha</th>
                <th>Total</th>
                <th>Tipo de Venta</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody data-testid="invoices-table-body">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} data-testid={`invoice-row-${invoice.numero_factura}`}>
                  <td className="font-medium">{invoice.numero_factura}</td>
                  <td className="text-sm text-slate-600">{invoice.ncf}</td>
                  <td className="font-mono text-blue-600 font-medium">{getInvoiceClientCode(invoice)}</td>
                  <td>
                    <div className="font-medium">{invoice.cliente.nombre}</div>
                    {invoice.cliente.rnc && <div className="text-xs text-slate-500">RNC: {invoice.cliente.rnc}</div>}
                  </td>
                  <td className="text-sm">{invoice.fecha}</td>
                  <td className="font-semibold text-green-600">{formatCurrency(invoice.total)}</td>
                  <td>
                    {invoice.es_credito ? (
                      // CRÉDITO
                      <div className="text-sm">
                        <div className="font-semibold text-orange-600">Crédito</div>
                        <div className="text-xs text-slate-500">Forma de pago: N/A</div>
                      </div>
                    ) : (
                      // CONTADO
                      <div className="text-sm">
                        <div className="font-semibold text-emerald-600">Contado</div>
                        <div className="text-xs text-slate-600 capitalize">
                          {invoice.tipo_pago === 'mixto' ? 'Pago Combinado' : 
                           invoice.tipo_pago === 'credito_favor' ? 'Nota de Crédito' :
                           invoice.tipo_pago === 'efectivo' ? 'Efectivo' :
                           invoice.tipo_pago === 'tarjeta' ? 'Tarjeta' :
                           invoice.tipo_pago === 'transferencia' ? 'Transferencia' :
                           invoice.tipo_pago}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                        invoice.status === 'activa' || invoice.status === 'pagada'
                          ? 'bg-green-100 text-green-700'
                          : invoice.status === 'pendiente'
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : invoice.status === 'devuelta_parcial'
                          ? 'bg-amber-100 text-amber-700'
                          : invoice.status === 'devuelta_total'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {invoice.status === 'activa' ? 'Activa' 
                        : invoice.status === 'pagada' ? 'Pagada'
                        : invoice.status === 'pendiente' ? 'Pendiente de Cobro'
                        : invoice.status === 'devuelta_parcial' ? 'Dev. Parcial'
                        : invoice.status === 'devuelta_total' ? 'Dev. Total'
                        : 'Anulada'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewInvoiceDetail(invoice)}
                        data-testid={`view-invoice-btn-${invoice.numero_factura}`}
                        title="Ver detalles"
                      >
                        <Eye size={16} />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`print-invoice-btn-${invoice.numero_factura}`}
                            title="Imprimir factura"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Printer size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handlePrintInvoice(invoice)}>
                            <Receipt className="mr-2" size={14} />
                            Formato POS (80mm)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => printInvoiceA4(invoice)}>
                            <FileText className="mr-2" size={14} />
                            Formato A4
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadInvoicePDF(invoice)}
                        data-testid={`download-invoice-pdf-${invoice.numero_factura}`}
                        title="Descargar PDF"
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      >
                        <Download size={16} />
                      </Button>
                      {canCancel && invoice.status === 'activa' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleCancelInvoice(invoice.id)}
                          data-testid={`cancel-invoice-btn-${invoice.numero_factura}`}
                          title="Anular factura"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredInvoices.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No se encontraron facturas
          </div>
        )}
      </div>

      {/* Invoice Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="invoice-detail-dialog">
          {selectedInvoice && (
            <>
              <DialogHeader>
                <DialogTitle>Detalle de Factura #{selectedInvoice.numero_factura}</DialogTitle>
                <DialogDescription>NCF: {selectedInvoice.ncf}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Cliente:</span> {selectedInvoice.cliente.nombre}
                  </div>
                  <div>
                    <span className="font-semibold">Fecha:</span>{' '}
                    {selectedInvoice.fecha}
                  </div>
                  {selectedInvoice.cliente.rnc && (
                    <div>
                      <span className="font-semibold">RNC:</span> {selectedInvoice.cliente.rnc}
                    </div>
                  )}
                  <div>
                    <span className="font-semibold">Cajero:</span> {selectedInvoice.cajero_nombre}
                  </div>
                  <div>
                    <span className="font-semibold">Forma de Pago:</span>{' '}
                    <span className="capitalize">
                      {selectedInvoice.tipo_pago === 'credito_favor' ? 'Nota de Crédito' :
                       selectedInvoice.tipo_pago === 'efectivo' ? 'Efectivo' :
                       selectedInvoice.tipo_pago === 'tarjeta' ? 'Tarjeta' :
                       selectedInvoice.tipo_pago === 'transferencia' ? 'Transferencia' :
                       selectedInvoice.tipo_pago === 'mixto' ? 'Pago Combinado' :
                       selectedInvoice.tipo_pago}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">Estado:</span>{' '}
                    <span className={
                      selectedInvoice.status === 'activa' || selectedInvoice.status === 'pagada' 
                        ? 'text-green-600' 
                        : selectedInvoice.status === 'pendiente'
                        ? 'text-amber-700 font-semibold'
                        : selectedInvoice.status === 'devuelta_parcial'
                        ? 'text-amber-600'
                        : selectedInvoice.status === 'devuelta_total'
                        ? 'text-orange-600'
                        : 'text-red-600'
                    }>
                      {selectedInvoice.status === 'activa' ? 'Activa' 
                        : selectedInvoice.status === 'pagada' ? 'Pagada'
                        : selectedInvoice.status === 'pendiente' ? 'Pendiente de Cobro'
                        : selectedInvoice.status === 'devuelta_parcial' ? 'Dev. Parcial'
                        : selectedInvoice.status === 'devuelta_total' ? 'Dev. Total'
                        : 'Anulada'}
                    </span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Items</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">Producto</th>
                        <th className="text-right p-2">Cant.</th>
                        <th className="text-right p-2">Precio</th>
                        <th className="text-right p-2">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items.map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-2">{item.producto_nombre}</td>
                          <td className="text-right p-2">{item.cantidad}</td>
                          <td className="text-right p-2">{formatCurrency(item.precio_unitario)}</td>
                          <td className="text-right p-2 font-semibold">{formatCurrency(item.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ITBIS (18%):</span>
                    <span>{formatCurrency(selectedInvoice.itbis)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span className="text-green-600">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                </div>

                {/* Botones de Impresión */}
                <div className="grid grid-cols-2 gap-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className="bg-blue-500 hover:bg-blue-600"
                        data-testid="print-invoice-btn"
                      >
                        <Printer className="mr-2" size={16} />
                        Imprimir
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => printInvoice(selectedInvoice)}>
                        <Receipt className="mr-2" size={14} />
                        Formato POS (80mm)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => printInvoiceA4(selectedInvoice)}>
                        <FileText className="mr-2" size={14} />
                        Formato A4
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    onClick={() => downloadInvoicePDF(selectedInvoice)}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="download-invoice-pdf-btn"
                  >
                    <Download className="mr-2" size={16} />
                    Descargar PDF
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Búsqueda de Clientes */}
      <Dialog open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Búsqueda Avanzada de Clientes</DialogTitle>
            <DialogDescription>Busque y seleccione un cliente registrado</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por código, nombre, RNC, cédula o teléfono..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Código</th>
                    <th className="text-left p-2">Nombre</th>
                    <th className="text-left p-2">RNC/Cédula</th>
                    <th className="text-left p-2">Teléfono</th>
                    <th className="text-right p-2">Crédito Disp.</th>
                    <th className="text-center p-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {customers
                    .filter(c => {
                      const search = customerSearchTerm.toLowerCase();
                      return (
                        c.nombre.toLowerCase().includes(search) ||
                        c.rnc?.toLowerCase().includes(search) ||
                        c.cedula?.toLowerCase().includes(search) ||
                        c.cedula_rnc?.toLowerCase().includes(search) ||
                        c.telefono?.includes(search) ||
                        c.codigo_cliente?.toLowerCase().includes(search)
                      );
                    })
                    .map(customer => (
                      <tr key={customer.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => selectCustomer(customer)}>
                        <td className="p-2 font-mono text-slate-600">{customer.codigo_cliente || 'N/A'}</td>
                        <td className="p-2 font-medium">{customer.nombre}</td>
                        <td className="p-2 text-slate-600">{customer.cedula_rnc || customer.rnc || customer.cedula || '-'}</td>
                        <td className="p-2 text-slate-600">{customer.telefono || '-'}</td>
                        <td className="p-2 text-right">
                          {customer.tiene_credito ? (
                            <span className="text-green-600 font-semibold">
                              {formatCurrency(customer.credito_disponible || 0)}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            size="sm"
                            onClick={() => selectCustomer(customer)}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            Seleccionar
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {customers.filter(c => {
                const search = customerSearchTerm.toLowerCase();
                return (
                  c.nombre.toLowerCase().includes(search) ||
                  c.rnc?.toLowerCase().includes(search) ||
                  c.cedula?.toLowerCase().includes(search) ||
                  c.cedula_rnc?.toLowerCase().includes(search) ||
                  c.telefono?.includes(search) ||
                  c.codigo_cliente?.toLowerCase().includes(search)
                );
              }).length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No se encontraron clientes
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Búsqueda Avanzada de Productos */}
      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen}>
        <DialogContent className="max-w-6xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Búsqueda Avanzada de Productos</DialogTitle>
            <DialogDescription>Busque productos por nombre, código o categoría</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por nombre, código interno, código de barras o categoría..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            <div className="border rounded-lg max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Cód. Interno</th>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-left p-2">Categoría</th>
                    <th className="text-right p-2">Precio</th>
                    <th className="text-center p-2">Stock</th>
                    <th className="text-center p-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProductsForSearch.map(product => {
                    const stockBajo = product.existencia < 10;
                    const enCarrito = cart.find(item => item.producto_id === product.id);
                    
                    return (
                      <tr key={product.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 text-slate-700 font-mono text-xs">{product.codigo_interno || '-'}</td>
                        <td className="p-2">
                          <div className="font-medium text-slate-800">{product.nombre}</div>
                          <div className="text-xs text-slate-500">{product.unidad_medida}</div>
                        </td>
                        <td className="p-2 text-slate-600">{product.categoria || '-'}</td>
                        <td className="p-2 text-right font-semibold text-green-600">{formatCurrency(product.precio_venta)}</td>
                        <td className="p-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            stockBajo ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {product.existencia}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            size="sm"
                            onClick={() => {
                              addToCart(product);
                              setProductSearchOpen(false);
                              setProductSearchTerm('');
                            }}
                            disabled={product.existencia === 0}
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            {enCarrito ? `Agregar (${enCarrito.cantidad})` : 'Agregar'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredProductsForSearch.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No se encontraron productos
                </div>
              )}
            </div>

            <div className="flex justify-between items-center text-sm text-slate-600 bg-slate-50 p-3 rounded">
              <span>💡 Haga clic en un producto para agregarlo al carrito</span>
              <span className="font-semibold">{filteredProductsForSearch.length} productos encontrados</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Pago Combinado */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar Pago Combinado</DialogTitle>
            <DialogDescription>Distribuya el total de la factura entre diferentes métodos de pago</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-100 p-4 rounded-lg text-center">
              <div className="text-sm text-slate-600 mb-1">Total a Pagar</div>
              <div className="text-2xl font-bold text-slate-900">{formatCurrency(calculateTotals().total)}</div>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="combined-efectivo">💵 Efectivo</Label>
                <Input
                  id="combined-efectivo"
                  type="number"
                  min="0"
                  step="0.01"
                  value={combinedPayments.efectivo}
                  onChange={(e) => setCombinedPayments({ ...combinedPayments, efectivo: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="combined-tarjeta">💳 Tarjeta</Label>
                <Input
                  id="combined-tarjeta"
                  type="number"
                  min="0"
                  step="0.01"
                  value={combinedPayments.tarjeta}
                  onChange={(e) => setCombinedPayments({ ...combinedPayments, tarjeta: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="combined-transferencia">🏦 Transferencia</Label>
                <Input
                  id="combined-transferencia"
                  type="number"
                  min="0"
                  step="0.01"
                  value={combinedPayments.transferencia}
                  onChange={(e) => setCombinedPayments({ ...combinedPayments, transferencia: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="combined-cheque">📝 Cheque</Label>
                <Input
                  id="combined-cheque"
                  type="number"
                  min="0"
                  step="0.01"
                  value={combinedPayments.cheque}
                  onChange={(e) => setCombinedPayments({ ...combinedPayments, cheque: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-600">Total Ingresado:</span>
                <span className={`font-bold ${
                  Math.abs(Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0) - calculateTotals().total) < 0.01
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {formatCurrency(Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0))}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Diferencia:</span>
                <span className={`font-bold ${
                  Math.abs(Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0) - calculateTotals().total) < 0.01
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {formatCurrency(Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0) - calculateTotals().total)}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setCombinedPayments({ efectivo: 0, tarjeta: 0, transferencia: 0, cheque: 0 });
                  setPaymentModalOpen(false);
                  setFormData({ ...formData, tipo_pago: 'efectivo' });
                }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                onClick={handleCombinedPaymentSave}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Búsqueda de Cotizaciones */}
      <Dialog open={quotationSearchOpen} onOpenChange={setQuotationSearchOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">🔍 Buscar Cotización</DialogTitle>
            <DialogDescription>
              Seleccione una cotización pendiente para convertirla en factura
            </DialogDescription>
          </DialogHeader>

          {loadingQuotations ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : quotations.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">No hay cotizaciones pendientes</p>
              <p className="text-slate-400 text-sm mt-2">
                Todas las cotizaciones han sido convertidas o canceladas
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <span className="font-semibold">💡 Importante:</span> Los precios se actualizarán automáticamente a los valores actuales si han cambiado desde la cotización.
              </div>

              <div className="border rounded-lg max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-3">N° Cotización</th>
                      <th className="text-left p-3">Cliente</th>
                      <th className="text-left p-3">Fecha</th>
                      <th className="text-right p-3">Total</th>
                      <th className="text-center p-3">Items</th>
                      <th className="text-center p-3">Validez</th>
                      <th className="text-center p-3">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotations.map((quotation) => {
                      const fechaCreacion = new Date(quotation.fecha);
                      const fechaVencimiento = new Date(fechaCreacion);
                      fechaVencimiento.setDate(fechaVencimiento.getDate() + quotation.validez_dias);
                      const diasRestantes = Math.ceil((fechaVencimiento - new Date()) / (1000 * 60 * 60 * 24));
                      const vencida = diasRestantes < 0;
                      const porVencer = diasRestantes <= 5 && diasRestantes >= 0;

                      return (
                        <tr key={quotation.id} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <span className="font-mono font-semibold text-blue-600">
                              {quotation.numero_cotizacion}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="font-medium text-slate-800">{quotation.cliente.nombre}</div>
                            {quotation.cliente.rnc && (
                              <div className="text-xs text-slate-500">RNC: {quotation.cliente.rnc}</div>
                            )}
                          </td>
                          <td className="p-3 text-slate-600">
                            {quotation.fecha}
                          </td>
                          <td className="p-3 text-right font-bold text-emerald-600">
                            {formatCurrency(quotation.total)}
                          </td>
                          <td className="p-3 text-center">
                            <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-semibold">
                              {quotation.items.length} items
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              vencida 
                                ? 'bg-red-100 text-red-800' 
                                : porVencer 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {vencida 
                                ? '❌ Vencida' 
                                : porVencer 
                                ? `⚠️ ${diasRestantes}d` 
                                : `✅ ${diasRestantes}d`
                              }
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              size="sm"
                              onClick={() => {
                                loadQuotationToForm(quotation);
                                setDialogOpen(true);
                              }}
                              className="bg-emerald-500 hover:bg-emerald-600"
                            >
                              <CheckCircle2 size={16} className="mr-1" />
                              Convertir
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center text-sm text-slate-600 bg-slate-50 p-3 rounded">
                <span>
                  💡 Las cotizaciones vencidas aún pueden convertirse, pero revise los precios
                </span>
                <span className="font-semibold">
                  {quotations.length} cotización{quotations.length !== 1 ? 'es' : ''} pendiente{quotations.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmación antes de crear factura */}
      <Dialog open={confirmCreateOpen} onOpenChange={(open) => { if (!creatingInvoice) setConfirmCreateOpen(open); }}>
        <DialogContent className="max-w-md" data-testid="confirm-create-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={22} />
              ¿Confirmar venta?
            </DialogTitle>
            <DialogDescription>
              {businessConfig?.facturas_pendientes_cobro_habilitado
                ? 'La factura quedará en estado "Pendiente de Cobro" y deberá completarse desde el módulo de Caja.'
                : 'Se registrará la venta y se imprimirá la factura.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Cliente:</span>
                <span className="font-semibold text-right max-w-[250px] truncate">{formData.cliente_nombre || '-'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Items:</span>
                <span className="font-semibold">{cart.length} producto{cart.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">NCF:</span>
                <span className="font-semibold">{formData.tipo_ncf}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Tipo de pago:</span>
                <span className="font-semibold">
                  {formData.es_credito
                    ? `Crédito (${formData.dias_credito} días)`
                    : formData.tipo_pago?.charAt(0).toUpperCase() + formData.tipo_pago?.slice(1)}
                </span>
              </div>
              <div className="flex justify-between text-lg pt-2 mt-2 border-t border-slate-200">
                <span className="font-bold">TOTAL:</span>
                <span className="font-bold text-emerald-600">
                  {formatCurrency(cart.reduce((sum, i) => sum + (i.subtotal || 0), 0))}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => setConfirmCreateOpen(false)}
              disabled={creatingInvoice}
              data-testid="cancel-create-invoice-btn"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={confirmAndCreateInvoice}
              disabled={creatingInvoice}
              data-testid="confirm-create-invoice-btn"
            >
              <CheckCircle2 size={16} className="mr-2" />
              {creatingInvoice ? 'Procesando...' : 'Sí, realizar venta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
